import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { ApiErrorSchema, type ApiError, type OrgSettings, type OrgProfile, type WritingPreferences } from '@/lib/schemas'
import { stripDiffMarkup } from '@/lib/utils'
import type { SourceHighlightData } from '@/stores/assistant-store'
import type { TenderOverview } from '@/types/tender-overview'
import type { ExtractedComplianceItem, ExtractionRunSummary } from '@/types/extraction'
import type { ChatAttachmentRef } from '@/types/assistant-attachments'
import type { BidOutcomeValue } from '@/lib/bid-lifecycle'
import type { OutputVersionWithDownload } from '@/types/output-version'
import type {
  LodgeSubmissionInput,
  SubmissionFilesResponse,
  SubmissionRecord,
} from '@/types/submission-files'

// ===================
// API Configuration
// ===================

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '/api'

// ===================
// Error Handling
// ===================

export class ApiRequestError extends Error {
  code: string
  details: Record<string, unknown> | undefined
  /** Correlation ID for this failing request — used for support tickets. */
  requestId: string | undefined
  /** HTTP status code of the failing response (undefined for network errors). */
  statusCode: number | undefined

  constructor(error: ApiError, statusCode?: number) {
    super(error.message)
    this.name = 'ApiRequestError'
    this.code = error.code
    this.details = error.details
    this.requestId = error.requestId
    this.statusCode = statusCode
  }

  /**
   * Whether this error should surface the requestId to the user.
   * Only shown for server errors (5xx) and network failures — 4xx validation
   * errors already have actionable messages and would add noise.
   */
  get shouldShowRequestId(): boolean {
    if (!this.requestId) return false
    if (this.code === 'NETWORK_ERROR') return true
    if (this.statusCode && this.statusCode >= 500) return true
    return false
  }

  /**
   * Short form of the request ID suitable for display in toasts.
   * Takes the first 13 characters (uuid first segment + partial second)
   * so users can dictate it without reading the full UUID.
   */
  get shortRequestId(): string {
    return this.requestId ? this.requestId.slice(0, 13) : ''
  }
}

function resolveApiBaseForDiagnostics(): string {
  try {
    return new URL(`${API_BASE_URL}`, window.location.origin).toString()
  } catch {
    return API_BASE_URL
  }
}

function handleApiError(error: unknown): never {
  if (error instanceof ApiRequestError) {
    throw error
  }

  const isNetworkFailure = (
    error instanceof TypeError
    && /(failed to fetch|networkerror|load failed|network request failed)/i.test(error.message)
  )

  if (isNetworkFailure) {
    const apiBase = resolveApiBaseForDiagnostics()
    const message = import.meta.env.DEV
      ? `Unable to connect to server (${apiBase}). Please check your network connection and VITE_API_BASE_URL.`
      : 'Unable to connect to server. Please check your network connection.'
    throw new ApiRequestError({
      code: 'NETWORK_ERROR',
      message,
    })
  }

  if (error instanceof Error) {
    throw new ApiRequestError({
      code: 'UNKNOWN_ERROR',
      message: error.message,
    })
  }

  throw new ApiRequestError({
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  })
}

// ===================
// Toast Helpers
// ===================

/**
 * Show a sonner error toast for an API error, automatically appending the
 * request correlation ID as a secondary description when available (server
 * errors and network failures). Customers can copy the ID from the toast to
 * include in support tickets.
 *
 * Usage:
 *   try { ... } catch (err) { toastApiError(err, 'Failed to save changes') }
 */
export function toastApiError(error: unknown, fallback: string): void {
  if (error instanceof ApiRequestError) {
    if (error.shouldShowRequestId) {
      toast.error(error.message, {
        description: `Error ID: ${error.shortRequestId} (click to copy full ID)`,
        action: {
          label: 'Copy',
          onClick: () => {
            if (error.requestId) {
              navigator.clipboard.writeText(error.requestId).catch(() => {})
            }
          },
        },
      })
      return
    }
    toast.error(error.message)
    return
  }

  if (error instanceof Error) {
    toast.error(error.message || fallback)
    return
  }

  toast.error(fallback)
}

// ===================
// Request Utilities
// ===================

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  params?: Record<string, string | number | boolean | undefined> | undefined
  headers?: HeadersInit
  /** HTTP statuses that are expected outcomes — skip console logging and toasts. The error is still thrown. */
  silentStatuses?: number[]
  /**
   * Opt-in client-side deadline, in milliseconds. Past it the request is
   * aborted and the call rejects with an ApiRequestError coded 'TIMEOUT' —
   * also when it is stuck before reaching the network (reading the auth
   * session). Requests that do not ask for one have no deadline.
   */
  timeoutMs?: number
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  if (options.timeoutMs === undefined) return sendApiRequest<T>(endpoint, options)
  return withDeadline(options.timeoutMs, (signal) => sendApiRequest<T>(endpoint, options, signal))
}

/**
 * Run `send` against a deadline. When it passes, the returned promise rejects
 * with a TIMEOUT error and `send`'s signal is aborted, so a stalled fetch lets
 * go of its connection. The timer is cleared as soon as `send` settles.
 */
async function withDeadline<T>(
  timeoutMs: number,
  send: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      // Reject first, so the caller sees the timeout rather than the abort error it causes.
      reject(new ApiRequestError({
        code: 'TIMEOUT',
        message: 'The server took too long to respond. Please try again.',
      }))
      controller.abort()
    }, timeoutMs)
  })
  try {
    return await Promise.race([send(controller.signal), deadline])
  } finally {
    clearTimeout(timer)
  }
}

async function sendApiRequest<T>(
  endpoint: string,
  options: RequestOptions,
  signal?: AbortSignal
): Promise<T> {
  const { method = 'GET', body, params, headers: customHeaders } = options

  try {
    // Build URL with query params
    const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin)

    // Debug logging (remove in production)
    if (import.meta.env.DEV) {
      console.log(`[API] ${method} ${url.toString()}`, body ? JSON.stringify(body) : '')
    }
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value))
        }
      })
    }

    // Get auth headers
    const authHeaders = await getAuthHeaders()

    // Make request
    const response = await fetch(url.toString(), {
      method,
      headers: {
        ...authHeaders,
        ...customHeaders,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const parsed = ApiErrorSchema.safeParse(errorData)
      // Fall back to the X-Request-Id response header when the body did
      // not include a requestId (e.g. early-stage server errors). Use
      // optional chaining so test mocks without a real Headers object
      // don't throw.
      const headerRequestId = response.headers?.get?.('X-Request-Id') ?? undefined

      // Preserve a non-error-shaped JSON body (e.g. reconciliation 409s send
      // `{ reason, target | proposal }` via a plain success-style body, not the
      // `{ code, message }` ApiError envelope). Attaching it to `details` lets
      // callers read the conflict payload off the thrown ApiRequestError.
      const rawBodyDetails =
        !parsed.success &&
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        Object.keys(errorData as Record<string, unknown>).length > 0
          ? (errorData as Record<string, unknown>)
          : undefined

      const apiError: ApiError = parsed.success
        ? { ...parsed.data, requestId: parsed.data.requestId ?? headerRequestId }
        : {
            code: `HTTP_${response.status}`,
            message: response.statusText || 'Request failed',
            requestId: headerRequestId,
            ...(rawBodyDetails ? { details: rawBodyDetails } : {}),
          }

      // MFA step-up interceptor (R12). When the backend returns 403 with
      // code='MFA_REQUIRED', dispatch a window event so a React listener
      // can navigate to /mfa-challenge?stepUp=1&returnTo=… without forcing
      // a full page reload. lib/api.ts is a non-React module — hence the
      // event indirection rather than a direct useNavigate call.
      if (response.status === 403 && apiError.code === 'MFA_REQUIRED' && typeof window !== 'undefined') {
        try {
          const returnTo = window.location.pathname + window.location.search
          window.dispatchEvent(new CustomEvent('mfa-step-up-required', { detail: { returnTo } }))
        } catch {
          // Non-fatal — caller still rejects with the original error.
        }
      }

      throw new ApiRequestError(apiError, response.status)
    }

    // Parse response - handle 204 No Content (empty body)
    if (response.status === 204) {
      return undefined as T
    }
    const data = await response.json()
    return data as T
  } catch (error) {
    // Aborted by its deadline: the caller already has the TIMEOUT error.
    if (signal?.aborted) throw error
    // If the status is an expected outcome, skip logging/toasts and rethrow directly.
    if (error instanceof ApiRequestError && options.silentStatuses?.includes(error.statusCode!)) {
      throw error
    }
    // Debug logging (remove in production)
    if (import.meta.env.DEV) {
      console.error(`[API] Error:`, error)
    }
    handleApiError(error)
  }
}

// ===================
// Convenience Methods
// ===================

export const api = {
  get: <T>(endpoint: string, params?: RequestOptions['params']) =>
    apiRequest<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body }),

  delete: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'DELETE', body }),
}

// ===================
// Document Upload Utilities (S3 Presigned URLs)
// ===================

export interface UploadUrlResponse {
  documentId: string
  uploadUrl: string
  s3Key: string
  expiresIn: number
}

export interface DownloadUrlResponse {
  downloadUrl: string
  expiresIn: number
}

/**
 * Request a presigned URL for uploading a file to S3
 */
export async function requestUploadUrl(
  filename: string,
  mimeType: string,
  size: number,
  bidId?: string
): Promise<UploadUrlResponse> {
  return api.post<UploadUrlResponse>('/documents/upload-url', {
    filename,
    mimeType,
    size,
    bidId,
  })
}

export interface ArtifactImageUploadResponse {
  imageId: string
  uploadUrl: string
  s3Key: string
  viewUrl: string
}

/**
 * Request a presigned URL + viewUrl for inserting an image into the draft editor.
 * The viewUrl is stamped into <img src> on insert; the rewriter refreshes it on
 * subsequent artifact loads.
 */
export async function requestArtifactImageUploadUrl(
  artifactId: string,
  filename: string,
  mimeType: string,
  size: number
): Promise<ArtifactImageUploadResponse> {
  return api.post<ArtifactImageUploadResponse>(
    `/artifacts/${artifactId}/images/upload-url`,
    { filename, mimeType, size }
  )
}

/**
 * Best-effort delete of a previously-uploaded artifact image. The editor does
 * not call this on every <img> deletion in V1 (orphans are accepted) — exposed
 * for future cleanup tooling.
 */
export async function deleteArtifactImage(artifactId: string, imageId: string): Promise<void> {
  await api.delete<void>(`/artifacts/${artifactId}/images/${imageId}`)
}

/**
 * Upload a file directly to S3 using a presigned URL with progress tracking
 */
export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new ApiRequestError({
          code: 'UPLOAD_ERROR',
          message: `Upload failed with status ${xhr.status}`,
        }))
      }
    })

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new ApiRequestError({
        code: 'NETWORK_ERROR',
        message: 'Network error during upload',
      }))
    })

    xhr.addEventListener('abort', () => {
      reject(new ApiRequestError({
        code: 'UPLOAD_ABORTED',
        message: 'Upload was aborted',
      }))
    })

    // Open and send request
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}

/**
 * Get a presigned URL for downloading a document
 */
export async function getDownloadUrl(documentId: string): Promise<string> {
  const response = await api.get<DownloadUrlResponse>(`/documents/${documentId}/download-url`)
  return response.downloadUrl
}

export interface RetryDocumentResponse {
  id: string
  processingStatus: 'pending'
  message: string
}

/**
 * Retry a failed document. The backend validates (failed-only, retryable,
 * max 3 attempts), resets the row to pending and queues processing in the
 * background — the response is 202 and the row is polled to completion.
 */
export async function retryDocumentProcessing(documentId: string): Promise<RetryDocumentResponse> {
  return api.post<RetryDocumentResponse>(`/documents/${documentId}/retry`)
}

/**
 * Get a presigned URL for inline viewing (no download prompt, 5-min expiry).
 *
 * For non-PDF originals (DOCX, XLSX, ...), the backend returns a converted
 * PDF as the viewUrl so the PDF viewer renders all formats consistently.
 * The original file is offered via `originalDownloadUrl` so users can still
 * grab the .docx / .xlsx if they need to edit or forward it.
 */
export type ConversionStatus =
  | 'pending'
  | 'converting'
  | 'ready'
  | 'failed'
  | 'not_required'

export interface ViewUrlResponse {
  viewUrl: string
  mimeType: string
  name: string
  size: number
  expiresIn: number
  /** Presigned URL for the original (non-PDF) file, when distinct from viewUrl. */
  originalDownloadUrl: string | null
  /** Original filename (e.g. "Conditions of Tender.docx") for the download button label. */
  originalFileName: string | null
  /** Lifecycle marker for the PDF conversion. Null for legacy docs pre-Migration 114. */
  conversionStatus: ConversionStatus | null
}

export async function getViewUrl(documentId: string): Promise<ViewUrlResponse> {
  return api.get<ViewUrlResponse>(`/documents/${documentId}/view-url`)
}

/**
 * Get a presigned URL for the HTML preview version (non-PDF formats)
 * Returns { htmlUrl: null } when no HTML preview exists
 */
export interface HtmlUrlResponse {
  htmlUrl: string | null
  expiresIn: number
}

export async function getHtmlUrl(documentId: string): Promise<HtmlUrlResponse> {
  return api.get<HtmlUrlResponse>(`/documents/${documentId}/html-url`)
}

// ===================
// Library Document Upload Utilities
// ===================

/**
 * Request a presigned URL for uploading a library document to S3
 */
export async function requestLibraryUploadUrl(
  filename: string,
  mimeType: string,
  size: number,
  folderId?: string | null,
  allowDuplicate?: boolean
): Promise<UploadUrlResponse> {
  return api.post<UploadUrlResponse>('/library/documents/upload-url', {
    filename,
    mimeType,
    size,
    folderId,
    ...(allowDuplicate ? { allowDuplicate: true } : {}),
  })
}

/**
 * The existing document a same-name upload collided with (F4). Carried in the
 * 409's `details` so the client can offer "new version of this" vs "keep both".
 */
export interface DuplicateLibraryDocument {
  id: string
  name: string
  version: number
  uploaderName: string | null
  createdAt: string
  size: number
}

/**
 * Narrow an upload failure to the same-name-same-folder collision, returning the
 * existing document. Any other error (or a malformed payload) yields null so the
 * caller falls through to ordinary error handling rather than opening a dialog
 * it cannot populate.
 */
export function parseDuplicateNameConflict(error: unknown): DuplicateLibraryDocument | null {
  if (!(error instanceof ApiRequestError) || error.statusCode !== 409) return null
  const details = error.details
  if (!details || details['reason'] !== 'duplicate_name') return null

  const existing = details['existingDocument'] as Partial<DuplicateLibraryDocument> | undefined
  if (!existing || typeof existing.id !== 'string' || typeof existing.name !== 'string') return null

  return {
    id: existing.id,
    name: existing.name,
    version: typeof existing.version === 'number' ? existing.version : 1,
    uploaderName: typeof existing.uploaderName === 'string' ? existing.uploaderName : null,
    createdAt: typeof existing.createdAt === 'string' ? existing.createdAt : '',
    size: typeof existing.size === 'number' ? existing.size : 0,
  }
}

/**
 * Create a library document record after successful S3 upload
 */
export interface CreateLibraryDocumentRequest {
  documentId: string
  name: string
  mimeType: string
  size: number
  s3Key: string
  folderId?: string | null
  /** Set only after the user picks "Keep both" in the collision dialog (F4). */
  allowDuplicate?: boolean
}

export interface LibraryDocumentResponse {
  id: string
  name: string
  type: string
  mimeType: string
  size: number
  s3Key: string
  organizationId: string
  uploadedBy: string
  folderId: string | null
  processingStatus: string
  createdAt: string
}

export async function createLibraryDocument(
  data: CreateLibraryDocumentRequest
): Promise<LibraryDocumentResponse> {
  return api.post<LibraryDocumentResponse>('/library/documents', data)
}

/**
 * Get a presigned URL for downloading a library document
 */
export async function getLibraryDownloadUrl(documentId: string): Promise<string> {
  const response = await api.get<DownloadUrlResponse>(`/library/documents/${documentId}/download-url`)
  return response.downloadUrl
}

/**
 * Get the current auth token for WebSocket connection
 */
export async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

// ===================
// Artifact API Utilities (Phase 1A)
// ===================

export interface OutlineSection {
  sectionKey: string
  title: string
  description?: string
  targetWordCount?: number
  /**
   * Where `targetWordCount` came from: a stated tender limit, or typed by a
   * person. There is no default length. Absent on outlines stored before
   * 2026-10 (their targets may be the old flat defaults).
   */
  targetWordCountBasis?: 'tender_limit' | 'user'
  notes?: string
  status?: 'not_started' | 'in_progress' | 'review' | 'completed'

  // Structure-proposal metadata (written by Layer-2 outline accept; all optional)
  /** AI guidance: what this section must achieve. */
  guidance?: string
  /** User-authored section notes — authoritative over guidance. */
  userNotes?: string
  mappedCriteriaIds?: string[]
  mappedComplianceIds?: string[]
  themeIds?: string[]
  citations?: SectionCitation[]
  source?: 'ai' | 'manual'
}

export interface SectionCitation {
  documentId: string
  documentName: string
  page: number | null
  quote: string
  signalGroup: 'instructions' | 'scope' | 'conditions'
}

export interface Outline {
  sections: OutlineSection[]
}

/**
 * `excel_schedule` artifacts are workbook fills, not prose: they carry a
 * `metadata.schedule` block and are read through the schedule routes below.
 * `word_form` artifacts are buyer Word forms filled in place (R3), read through
 * the form routes.
 */
export type ArtifactType = 'narrative_document' | 'questionnaire' | 'excel_schedule' | 'word_form'

export interface BidArtifact {
  id: string
  organizationId: string
  bidId: string
  name: string
  description: string | null
  metadata?: Record<string, unknown> | null
  artifactType: ArtifactType
  scopeKind: 'unit' | 'artifact'
  outlineJson: Outline | null
  outlineVersion: number
  status: 'draft' | 'in_progress' | 'pending_review' | 'changes_requested' | 'finalized'
  currentReviewId: string | null
  ownerId: string | null
  ownerName: string | null
  ownerAvatarUrl: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

export type ArtifactReviewDecision = 'approve' | 'request_changes'
export type ArtifactReviewStatus = 'pending' | 'completed' | 'cancelled'

export interface ArtifactReview {
  id: string
  organizationId: string
  bidId: string
  artifactId: string
  round: number
  reviewerId: string
  reviewerEmail: string | null
  reviewerFullName: string | null
  reviewerAvatarUrl: string | null
  requestedBy: string
  requestedByEmail: string | null
  requestedByFullName: string | null
  requestedAt: string
  status: ArtifactReviewStatus
  decision: ArtifactReviewDecision | null
  decidedAt: string | null
  overallNotes: string | null
  createdAt: string
  updatedAt: string
}

export interface SectionStatus {
  sectionKey: string
  title: string
  status: string
  wordCount: number | null
  targetWords: number | null
  updatedAt: string | null
}

export interface CreateBidArtifact {
  name: string
  description?: string | null
  metadata?: Record<string, unknown> | null
  scopeKind?: 'unit' | 'artifact'
  artifactType?: ArtifactType
  /** Required for `excel_schedule` (the workbook) and `word_form` (the Word file): a bid document. */
  sourceDocumentId?: string
}

export interface UpdateBidArtifact {
  name?: string
  description?: string | null
  metadata?: Record<string, unknown> | null
  status?: BidArtifact['status']
  ownerId?: string | null
}

export interface ArtifactSubmissionLink {
  submissionItemId: string
  submissionItemTitle: string
  limit: string | null
  sectionKey: string | null
  sectionTitle: string | null
}

/**
 * List artifacts for a bid
 */
export async function listArtifacts(bidId: string): Promise<{ artifacts: BidArtifact[] }> {
  return api.get<{ artifacts: BidArtifact[] }>(`/bids/${bidId}/artifacts`)
}

/**
 * Get a single artifact
 */
/** A staged suggestion waiting on a document, for the overview's list (R1 W2). */
export interface ArtifactPendingSuggestion {
  proposalId: string
  sectionKey: string
  heading: string
  /**
   * Words the section would hold once accepted (counted as saved sections
   * are). Absent from an older server. The sections table shows it where
   * nothing is saved yet.
   */
  wordCount?: number
}

export async function getArtifact(
  artifactId: string
): Promise<{ artifact: BidArtifact; pendingSuggestions?: ArtifactPendingSuggestion[] }> {
  return api.get<{ artifact: BidArtifact; pendingSuggestions?: ArtifactPendingSuggestion[] }>(`/artifacts/${artifactId}`)
}

/**
 * Create a new artifact
 */
export async function createArtifact(
  bidId: string,
  data: CreateBidArtifact
): Promise<{ artifact: BidArtifact }> {
  return api.post<{ artifact: BidArtifact }>(`/bids/${bidId}/artifacts`, data)
}

/**
 * Update an artifact
 */
export async function updateArtifact(
  artifactId: string,
  data: UpdateBidArtifact
): Promise<{ artifact: BidArtifact }> {
  return api.patch<{ artifact: BidArtifact }>(`/artifacts/${artifactId}`, data)
}

/**
 * Delete an artifact
 */
export async function deleteArtifact(artifactId: string): Promise<void> {
  return api.delete(`/artifacts/${artifactId}`)
}

/**
 * Reopen a finalized artifact
 */
export async function reopenArtifact(artifactId: string): Promise<{ artifact: BidArtifact }> {
  return api.post<{ artifact: BidArtifact }>(`/artifacts/${artifactId}/reopen`, {})
}

// ===================
// Document-level review workflow
// ===================

export async function submitArtifactForReview(
  artifactId: string,
  reviewerId: string,
): Promise<{ review: ArtifactReview; artifact: BidArtifact }> {
  return api.post(`/artifacts/${artifactId}/submit-for-review`, { reviewerId })
}

export async function decideArtifactReview(
  artifactId: string,
  reviewId: string,
  decision: ArtifactReviewDecision,
  overallNotes?: string | null,
): Promise<{ review: ArtifactReview; artifact: BidArtifact; sectionsFlipped: number }> {
  return api.post(`/artifacts/${artifactId}/reviews/${reviewId}/decide`, {
    decision,
    overallNotes: overallNotes ?? undefined,
  })
}

export async function listArtifactReviews(
  artifactId: string,
): Promise<{ reviews: ArtifactReview[] }> {
  return api.get(`/artifacts/${artifactId}/reviews`)
}

export async function cancelArtifactReview(
  artifactId: string,
): Promise<{ review: ArtifactReview; artifact: BidArtifact }> {
  return api.post(`/artifacts/${artifactId}/cancel-review`, {})
}

export async function getArtifactSubmissionLinks(
  artifactId: string
): Promise<{ links: ArtifactSubmissionLink[] }> {
  return api.get<{ links: ArtifactSubmissionLink[] }>(`/artifacts/${artifactId}/submission-links`)
}

// ===================
// Excel Schedule Fill
// Routes hang off `excel_schedule` artifacts. Shapes mirror
// `backend/src/handlers/artifacts.ts` — see `@/types/schedule`.
// ===================

import type {
  FillDecision,
  FillProposal,
  FillProposalCounts,
  FillProposalStatus,
  FillSource,
  ScanResult,
  ScheduleExport,
  ScheduleExportWithUrl,
  ScheduleMetadata,
  ScheduleOutline,
} from '@/types/schedule'

export type {
  CellModel,
  FillDecision,
  FillProposal,
  FillProposalCounts,
  FillProposalStatus,
  FillSource,
  FillTarget,
  FillValueType,
  ScanError,
  ScanResult,
  ScheduleExport,
  ScheduleExportWithUrl,
  ScheduleIngestStatus,
  ScheduleMetadata,
  ScheduleOutline,
  SheetModel,
  SheetSummary,
  WorkbookModel,
} from '@/types/schedule'

export interface ScheduleInfo {
  schedule: ScheduleMetadata
  outline: ScheduleOutline
  /** Presigned S3 GET for the WorkbookModel JSON. Null until ingest succeeds. */
  modelUrl: string | null
  counts: FillProposalCounts
}

export async function getSchedule(artifactId: string): Promise<ScheduleInfo> {
  return api.get<ScheduleInfo>(`/artifacts/${artifactId}/schedule`)
}

/**
 * Re-dispatch the ingest worker for a schedule stuck on 'pending' or 'failed'.
 * Responds 202 with the artifact carrying a freshly-pending schedule block.
 */
export async function reingestSchedule(
  artifactId: string
): Promise<{ artifact: BidArtifact }> {
  return api.post<{ artifact: BidArtifact }>(`/artifacts/${artifactId}/schedule/reingest`, {})
}

export async function getFillProposals(
  artifactId: string,
  status?: FillProposalStatus
): Promise<{ proposals: FillProposal[]; counts: FillProposalCounts }> {
  return api.get<{ proposals: FillProposal[]; counts: FillProposalCounts }>(
    `/artifacts/${artifactId}/fill-proposals`,
    status ? { status } : undefined
  )
}

/**
 * A value the user typed straight into a cell the agent never touched. Staged and
 * accepted server-side in one step, through the same validation and audit trail as
 * an agent fill — the grid never writes the workbook directly.
 */
export async function createManualFill(
  artifactId: string,
  input: { sheet: string; address: string; value: string }
): Promise<{ proposal: FillProposal; counts: FillProposalCounts }> {
  return api.post<{ proposal: FillProposal; counts: FillProposalCounts }>(
    `/artifacts/${artifactId}/fill-proposals`,
    input
  )
}

export async function decideFillProposal(
  artifactId: string,
  proposalId: string,
  decision: FillDecision,
  editedValue?: string
): Promise<{ proposal: FillProposal }> {
  return api.post<{ proposal: FillProposal }>(
    `/artifacts/${artifactId}/fill-proposals/${proposalId}`,
    editedValue === undefined ? { decision } : { decision, editedValue }
  )
}

export async function decideAllFillProposals(
  artifactId: string,
  decision: FillDecision,
  /** `sheet` for Excel schedules; `scheduleId` for one schedule of a Word form. */
  filter?: { sheet?: string; source?: FillSource; scheduleId?: string }
): Promise<{ decided: number }> {
  return api.post<{ decided: number }>(
    `/artifacts/${artifactId}/fill-proposals/decide-all`,
    { decision, ...filter }
  )
}

export interface ScheduleExportResult {
  export: ScheduleExport
  downloadUrl: string
  scan: ScanResult
  /** Proposals still awaiting a decision — the export did NOT include them. */
  pendingCount: number
  /** Accepted fills that could not be written (formula / protected / bad type). */
  rejectedCount: number
}

/**
 * Apply every accepted fill and mint a downloadable .xlsx. Rejects with a 400
 * ('No accepted fills to apply') when nothing new has been accepted.
 */
export async function exportSchedule(artifactId: string): Promise<ScheduleExportResult> {
  return api.post<ScheduleExportResult>(`/artifacts/${artifactId}/schedule/export`, {})
}

export async function getScheduleExports(
  artifactId: string
): Promise<{ exports: ScheduleExportWithUrl[] }> {
  return api.get<{ exports: ScheduleExportWithUrl[] }>(
    `/artifacts/${artifactId}/schedule/exports`
  )
}

// ===================
// Word Form Fill (R3 contract §4)
// Routes hang off `word_form` artifacts and mirror the Excel schedule routes.
// Answers reuse the fill-proposal routes above with a `docx_field` target. See
// `@/types/word-form`; error reasons map to plain copy in `@/lib/word-form`.
// ===================

import type {
  DocxFieldFillTarget,
} from '@/types/schedule'
import type {
  FormField,
  FormHeading,
  FormSchedule,
  FormScheduleCorrection,
  OutputVersionWithUrl,
  WordFormExportResult,
  WordFormMetadata,
} from '@/types/word-form'

export type {
  FormScheduleCorrection,
  OutputVersion,
  OutputVersionWithUrl,
  WordFormExportResult,
} from '@/types/word-form'

export interface WordFormInfo {
  artifact: BidArtifact
  metadata: WordFormMetadata
  schedules: FormSchedule[]
  fields: FormField[]
  /** Candidate split / add points. Empty until the form is read. */
  headings: FormHeading[]
}

/**
 * Create the buyer form for a Word bid document — or get the one it already
 * has (one form per source document): 201 `{ artifact }` or 200
 * `{ artifact, existing: true }`. Reading the file runs in the background.
 */
export async function createWordForm(
  bidId: string,
  input: { sourceDocumentId: string; name: string }
): Promise<{ artifact: BidArtifact; existing?: boolean }> {
  return api.post<{ artifact: BidArtifact; existing?: boolean }>(`/bids/${bidId}/artifacts`, {
    artifactType: 'word_form',
    sourceDocumentId: input.sourceDocumentId,
    name: input.name,
  })
}

export async function getWordForm(artifactId: string): Promise<WordFormInfo> {
  return api.get<WordFormInfo>(`/artifacts/${artifactId}/form`)
}

/** Read the buyer file again (202). Answers survive. */
export async function reingestWordForm(
  artifactId: string
): Promise<{ metadata: WordFormMetadata; artifact: BidArtifact }> {
  return api.post<{ metadata: WordFormMetadata; artifact: BidArtifact }>(
    `/artifacts/${artifactId}/form/reingest`,
    {}
  )
}

/**
 * A correction to the found schedules. Refusals are 400/404 with
 * `details.reason` (see `scheduleCorrectionMessage`).
 */
export async function correctFormSchedules(
  artifactId: string,
  correction: FormScheduleCorrection
): Promise<{ schedules: FormSchedule[] }> {
  return api.patch<{ schedules: FormSchedule[] }>(
    `/artifacts/${artifactId}/form/schedules`,
    correction
  )
}

/**
 * Write every accepted answer into a copy of the buyer's file, through the
 * check. 400 = nothing could be written (`details.rejected`); 422
 * `FORM_CHECK_FAILED` = the check failed and no file was kept.
 */
export async function exportWordForm(artifactId: string): Promise<WordFormExportResult> {
  return api.post<WordFormExportResult>(`/artifacts/${artifactId}/form/export`, {})
}

/** Ready completed copies only, newest first. */
export async function getWordFormExports(
  artifactId: string
): Promise<{ exports: OutputVersionWithUrl[] }> {
  return api.get<{ exports: OutputVersionWithUrl[] }>(`/artifacts/${artifactId}/form/exports`)
}

/**
 * An answer typed into a form field that has no proposal. Staged and accepted
 * server-side in one step. Refusals are 400 with `details.reason`
 * (see `fillRefusalMessage`).
 */
export async function createManualDocxFill(
  artifactId: string,
  input: { target: DocxFieldFillTarget; value: string | boolean }
): Promise<{ proposal: FillProposal; counts: FillProposalCounts }> {
  return api.post<{ proposal: FillProposal; counts: FillProposalCounts }>(
    `/artifacts/${artifactId}/fill-proposals`,
    input
  )
}

/** One Word tender file with schedules, for the Tender files list. */
export interface BidScheduleDocument {
  documentId: string
  scheduleCount: number
  wordFormArtifactId: string | null
}

/**
 * Schedule counts for every Word tender file of a bid — one call for the
 * whole Tender files list. Only documents with at least one schedule.
 */
export async function getBidSchedules(
  bidId: string
): Promise<{ documents: BidScheduleDocument[] }> {
  return api.get<{ documents: BidScheduleDocument[] }>(`/bids/${bidId}/schedules`)
}

export interface DocumentSchedulesInfo {
  schedules: FormSchedule[]
  wordFormArtifactId: string | null
}

/** Schedules found inside one tender file — the schedule panel. */
export async function getDocumentSchedules(
  bidId: string,
  documentId: string
): Promise<DocumentSchedulesInfo> {
  return api.get<DocumentSchedulesInfo>(`/bids/${bidId}/documents/${documentId}/schedules`)
}

// ===================
// Response Structure Proposals (Specs/response-structure-proposal-spec.md)
// Layer 1: bid-level document proposals. Routes ride existing artifact
// endpoints via body-shape / query-param disambiguation (CFN resource cap).
// ===================

export type TriageClass = 'response' | 'attachment' | 'form' | 'acknowledge'
export type ProposedDocumentType = 'multi-section' | 'single' | 'questionnaire'

export interface ProposedDocument {
  key: string
  submissionRequirementId: string | null
  name: string
  description: string
  documentType: ProposedDocumentType
  formatNote: string | null
  wordBudget: number | null
  rationale: string
}

export interface ExcludedProposalItem {
  submissionRequirementId: string
  title: string
  triageClass: Exclude<TriageClass, 'response'>
  reason: string
}

export interface DocumentsProposal {
  proposedDocuments: ProposedDocument[]
  excludedItems: ExcludedProposalItem[]
  notes: string[]
}

export type StructureGenerationStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'failed'
  | 'accepted'
  | 'superseded'

export interface StructureGeneration {
  id: string
  bidId: string
  kind: 'documents' | 'outline' | 'questions'
  artifactId: string | null
  status: StructureGenerationStatus
  error: string | null
  proposal: DocumentsProposal | null
  createdArtifactIds: string[]
  createdAt: string
  updatedAt: string
}

export async function startStructureProposal(
  bidId: string
): Promise<{ generationId: string; status: string }> {
  return api.post(`/bids/${bidId}/artifacts`, { _structureProposal: true })
}

export async function getStructureProposal(bidId: string): Promise<StructureGeneration | null> {
  return apiRequest<StructureGeneration | null>(`/bids/${bidId}/artifacts`, {
    params: { structureProposal: 'latest' },
  })
}

export async function acceptStructureProposal(
  bidId: string,
  generationId: string,
  documents: Array<Omit<ProposedDocument, 'rationale'>>
): Promise<{ artifactIds: string[] }> {
  return api.post(`/bids/${bidId}/artifacts`, {
    _structureProposalAccept: { generationId, documents },
  })
}

export async function discardStructureProposal(
  bidId: string,
  generationId: string
): Promise<{ discarded: boolean }> {
  return api.post(`/bids/${bidId}/artifacts`, {
    _structureProposalDiscard: { generationId },
  })
}

// ===================
// Layer 2: per-document outline / question proposals
// (rides PATCH/GET /artifacts/{id}/outline via body/query disambiguation)
// ===================

export interface ProposedSection {
  key: string
  title: string
  suggestedWordCount: number | null
  citations: SectionCitation[]
  mappedCriteriaIds: string[]
  mappedComplianceIds: string[]
  themeIds: string[]
  guidance: string
}

export interface OutlineProposal {
  sections: ProposedSection[]
  structureBasis: 'prescribed' | 'criteria' | 'scope'
  basisNote: string
  notes: string[]
}

export interface ProposedQuestion {
  key: string
  number: string | null
  text: string
  groupTitle: string | null
  wordLimit: number | null
  sourceQuote: string
  sourcePage: number | null
  sourceDocumentId: string | null
}

export interface QuestionsProposal {
  questions: ProposedQuestion[]
  extraction: {
    status: 'extracted' | 'fallback'
    sourceDocumentId: string | null
    fallbackReason: 'not_found' | 'low_confidence' | 'unsupported_format' | null
  }
  notes: string[]
}

export interface OutlineGeneration {
  id: string
  bidId: string
  kind: 'outline' | 'questions'
  artifactId: string
  status: StructureGenerationStatus
  /** Diagnostic text for logs. Not user-facing — show copy chosen by `failureReason`. */
  error: string | null
  /**
   * Why a `failed` generation failed, when the user is told specifically:
   * `output_limit` (the AI stopped at its output cap) or `timed_out` (the run
   * gave up at its deadline, or was found stuck). Null otherwise. Optional
   * because a backend from before this field omits it.
   */
  failureReason?: 'output_limit' | 'timed_out' | null
  proposal: OutlineProposal | QuestionsProposal | null
  createdArtifactIds: string[]
  createdAt: string
  updatedAt: string
}

export interface AcceptOutlineSection {
  key: string
  title: string
  suggestedWordCount: number | null
  mappedCriteriaIds: string[]
  mappedComplianceIds: string[]
  themeIds: string[]
  guidance: string
  userNotes: string
  citations: SectionCitation[]
  source: 'ai' | 'manual'
}

export async function startOutlineProposal(
  artifactId: string
): Promise<{ generationId: string; status: string }> {
  return api.patch(`/artifacts/${artifactId}/outline`, { _proposeOutline: true })
}

export async function getOutlineProposal(artifactId: string): Promise<OutlineGeneration | null> {
  return apiRequest<OutlineGeneration | null>(`/artifacts/${artifactId}/outline`, {
    params: { proposal: 'latest' },
  })
}

/** A non-blocking issue the accept found. Today only same-title collisions. */
export interface OutlineAcceptWarning {
  sectionKey: string
  title: string
  code: 'duplicate_title'
  message: string
}

export interface AcceptProposalResult {
  artifactId: string
  /** Outline path: the document total after the merge. */
  sectionCount?: number
  /** Outline path: what THIS accept appended (equals `sectionCount` on a first run). */
  addedSectionCount?: number
  warnings?: OutlineAcceptWarning[]
  /** Questions path. */
  questionCount?: number
}

/**
 * Accept a Layer-2 proposal. `expectedOutlineVersion` guards the outline write
 * the same way `updateArtifactOutline` does — the reviewer's outline can move
 * under them (Add Section stays live during review), and a blind accept would
 * replace the whole array and GC the other writer's requirement links.
 *
 * ⚠ It is **required**. Passing it optionally let the caller silently disable
 * the CAS: two concurrent accepts of the same `complete` generation would both
 * append, since the handler's `status !== 'complete'` check is TOCTOU.
 */
export async function acceptOutlineProposal(
  artifactId: string,
  generationId: string,
  payload: { sections: AcceptOutlineSection[] } | { questions: Array<{ key: string; number: string | null; text: string; groupTitle: string | null; wordLimit: number | null }> },
  expectedOutlineVersion: number
): Promise<AcceptProposalResult> {
  return api.patch(`/artifacts/${artifactId}/outline`, {
    _acceptProposal: {
      generationId,
      ...payload,
      expectedOutlineVersion,
    },
  })
}

export async function discardOutlineProposal(
  artifactId: string,
  generationId: string
): Promise<{ discarded: boolean }> {
  return api.patch(`/artifacts/${artifactId}/outline`, {
    _discardProposal: { generationId },
  })
}

export async function generateTenderSummary(
  bidId: string
): Promise<{ summary: string }> {
  return api.post<{ summary: string }>(`/bids/${bidId}/tender-summary`, {})
}

// ===================
// Requirement Coverage (Specs/compliance-checklist-spec.md)
//
// Rides the existing /bids/{id}/compliance-items route via ?coverage=1 and
// body-shape flags (_link/_unlink/_suggest) — the CFN 500-resource ceiling
// forbids new API resources.
// ===================

export type CoverageStatus = 'unmapped' | 'mapped' | 'drafting' | 'in_review' | 'addressed'

export interface CoverageLink {
  id: string
  artifactId: string
  artifactName: string
  sectionKey: string
  sectionTitle: string
  source: 'ai' | 'manual'
}

export interface CoverageRow {
  requirementId: string
  status: CoverageStatus
  links: CoverageLink[]
  /** Criteria only: true for parent rows whose status is a rollup. */
  isRollup?: boolean
}

export interface CoverageSummary {
  total: number
  unmapped: number
  mapped: number
  drafting: number
  inReview: number
  addressed: number
}

export interface CoveragePayload {
  compliance: CoverageRow[]
  criteria: CoverageRow[]
  complianceSummary: CoverageSummary
  criteriaSummary: CoverageSummary
}

export interface CoverageMappingSuggestion {
  requirementId: string
  requirementKind: 'compliance' | 'criterion'
  artifactId: string
  artifactName: string
  sectionKey: string
  sectionTitle: string
  reason: string
}

export interface CreateCoverageLinkInput {
  complianceItemId?: string
  evaluationCriterionId?: string
  artifactId: string
  sectionKey: string
  /** 'ai' when accepting an AI mapping suggestion — drives the ✦ on chips. */
  source?: 'ai' | 'manual'
}

/** Raw link row as returned by link creation (no display names — those come from getCoverage). */
export interface RequirementSectionLink {
  id: string
  bidId: string
  complianceItemId: string | null
  evaluationCriterionId: string | null
  artifactId: string
  sectionKey: string
  source: 'ai' | 'manual'
}

export async function getCoverage(bidId: string): Promise<CoveragePayload> {
  return api.get<CoveragePayload>(`/bids/${bidId}/compliance-items`, { coverage: '1' })
}

/** Fetch the persisted tender overview for a bid (null when not yet generated). */
export async function getTenderOverview(
  bidId: string
): Promise<{ overview: TenderOverview | null }> {
  return api.get<{ overview: TenderOverview | null }>(`/bids/${bidId}/tender-overview`)
}

export async function createCoverageLink(
  bidId: string,
  input: CreateCoverageLinkInput
): Promise<{ link: RequirementSectionLink }> {
  return api.post(`/bids/${bidId}/compliance-items`, { _link: input })
}

export async function deleteCoverageLink(
  bidId: string,
  linkId: string
): Promise<{ deleted: boolean }> {
  return api.post(`/bids/${bidId}/compliance-items`, { _unlink: { linkId } })
}

export async function suggestCoverageMappings(
  bidId: string,
  scope?: { requirementId?: string; sectionKey?: string }
): Promise<{ suggestions: CoverageMappingSuggestion[] }> {
  return api.post(`/bids/${bidId}/compliance-items`, { _suggest: scope ?? {} })
}

// ===================
// Compliance positions + AI assessment
//
// Four functions, two routes. The read (`getComplianceMatrixItems`) rides the
// existing extraction GET; the other three multiplex on the compliance-items
// POST via a body flag, same as _link / _suggest above. Nothing here gets its
// own route — the CFN 500-resource ceiling forbids new API resources.
// ===================

/**
 * Active compliance-matrix items with their declared positions. Rides the
 * existing extraction GET — for surfaces outside the extraction store (the
 * Mark-as-Submitted dialog) that need position counts without store wiring.
 */
export async function getComplianceMatrixItems(
  bidId: string
): Promise<ExtractedComplianceItem[]> {
  const data = await api.get<{ items: ExtractedComplianceItem[] }>(
    `/bids/${bidId}/extraction/compliance_matrix`
  )
  return Array.isArray(data?.items) ? data.items : []
}

/**
 * Pin the current AI position on these items as a human decision. The server
 * flips `positionSource` to 'manual', which takes them out of reassessment.
 */
export async function confirmCompliancePositions(
  bidId: string,
  itemIds: string[]
): Promise<{ confirmed: number }> {
  return api.post(`/bids/${bidId}/compliance-items`, { _confirm: { itemIds } })
}

/**
 * Fire-and-forget: kick off an assessment run for the whole active matrix, or
 * an explicit subset. Returns as soon as the worker is invoked (202) — the run
 * row appears a moment later, so callers poll `getAssessmentRun`.
 *
 * `started: false` means nothing was queued (the server-side kill switch is
 * off). No run row will ever appear, so the caller must NOT start polling —
 * it would spin until the poll deadline and then report a timeout that never
 * happened.
 */
export async function startComplianceAssessment(
  bidId: string,
  itemIds?: string[]
): Promise<{ started: boolean; reason?: string }> {
  return api.post(`/bids/${bidId}/compliance-items`, {
    _assess: itemIds ? { itemIds } : {},
  })
}

export interface ComplianceStaleness {
  latestLibraryChangeAt: string | null
  latestAssessedAt: string | null
}

/**
 * Two timestamps the caller compares itself to decide whether the library has
 * moved on since the last assessment.
 *
 * Returns `null` for a read-only member: the endpoint sits behind the shared
 * write guard on `POST /compliance-items`, so a viewer gets a 403 on what is
 * logically a read. That is not an error worth showing anyone — it just means
 * no staleness signal is available, and the caller should render no prompt.
 */
export async function getComplianceStaleness(
  bidId: string
): Promise<ComplianceStaleness | null> {
  try {
    return await apiRequest<ComplianceStaleness>(`/bids/${bidId}/compliance-items`, {
      method: 'POST',
      body: { _staleness: {} },
      silentStatuses: [403],
    })
  } catch (error) {
    if (error instanceof ApiRequestError && error.statusCode === 403) return null
    throw error
  }
}

/**
 * Newest `compliance_assessment` run for a bid, or null if it has never been
 * assessed. `GET /extraction-runs` returns the bid's runs unfiltered and
 * newest-first; we scan rather than trust the order.
 */
export async function getAssessmentRun(
  bidId: string
): Promise<ExtractionRunSummary | null> {
  const runs = await api.get<ExtractionRunSummary[]>(`/bids/${bidId}/extraction-runs`)
  if (!Array.isArray(runs)) return null
  let latest: ExtractionRunSummary | null = null
  for (const run of runs) {
    if (run.category !== 'compliance_assessment') continue
    if (!latest || run.createdAt > latest.createdAt) latest = run
  }
  return latest
}

/**
 * Brief-open scaffold (§3.1a): relevance-ordered theme + (on demand) document
 * suggestions for a unit. Rides the compliance-items POST (CFN-safe).
 */
export interface SectionScaffold {
  themeSuggestions: PlanSuggestion[]
  documentSuggestions: PlanSuggestion[]
}

export async function suggestBriefScaffold(
  bidId: string,
  input: { artifactId: string; sectionKey: string; includeDocuments?: boolean }
): Promise<SectionScaffold> {
  return api.post(`/bids/${bidId}/compliance-items`, { _suggestScaffold: input })
}

// ===================
// Next-Best-Action (Specs/bid-next-best-action-spec.md)
//
// Rides the existing /bids/{id} routes via ?next-actions=1 and the
// _dismissInsight body flag — the CFN 500-resource ceiling forbids new
// API resources.
// ===================

export type NextActionSeverity = 'critical' | 'attention' | 'positive'

export interface NextActionInsight {
  id: string
  ruleId: string
  severity: NextActionSeverity
  audience: 'owner' | 'member' | 'all'
  message: string
  actionLabel: string
  actionUrl: string
  relatedArtifactId: string | null
  relatedSectionId: string | null
  computedAt: string
}

export async function getNextActions(bidId: string): Promise<{ insights: NextActionInsight[] }> {
  return api.get(`/bids/${bidId}`, { 'next-actions': '1' })
}

export async function dismissNextAction(
  bidId: string,
  insightId: string,
  ruleId: string
): Promise<{ dismissed: boolean }> {
  return api.patch(`/bids/${bidId}`, { _dismissInsight: { insightId, ruleId } })
}

// ===================
// Submission Harvest + Outcome Capture (Specs/win-loss-harvest-spec.md)
//
// Rides the existing /bids/{id} routes via ?harvest-status=1 and the
// _harvest/_outcome body flags — the CFN 500-resource ceiling forbids new
// API resources.
// ===================

export type { BidOutcomeValue }

export interface HarvestArtifactStatus {
  artifactId: string
  name: string
  artifactType: string
  eligible: boolean
  ineligibleReason: string | null
  harvestedDocumentId: string | null
  lastHarvestedAt: string | null
}

export type PrimaryOutcomeReason =
  | 'price_value'
  | 'methodology'
  | 'experience'
  | 'personnel'
  | 'incumbency'
  | 'compliance'
  | 'social_local'
  | 'other'

export interface BidOutcome {
  id: string
  bidId: string
  /** R4 (K3): any of the five outcomes; result fields only for won or lost. */
  outcome: BidOutcomeValue
  score: number | null
  rank: number | null
  totalBidders: number | null
  primaryReason: PrimaryOutcomeReason | null
  feedbackText: string | null
  debriefDocumentId: string | null
  recordedAt: string
}

export interface HarvestStatusResponse {
  bidStatus: string
  /** A restricted bid needs the user's confirmation before it is harvested. */
  bidVisibility: 'org' | 'restricted'
  autoHarvestEnabled: boolean
  outcome: BidOutcome | null
  artifacts: HarvestArtifactStatus[]
}

export async function getHarvestStatus(bidId: string): Promise<HarvestStatusResponse> {
  return api.get(`/bids/${bidId}`, { 'harvest-status': '1' })
}

// A restricted bid is only harvested with `confirmRestrictedLibrary: true`;
// without it the server answers 409 RESTRICTED_BID_HARVEST_NEEDS_CONFIRMATION.
export interface HarvestRequestOptions {
  /** The user confirmed saving a restricted bid to the org-wide library. */
  confirmRestrictedLibrary?: boolean
}

/** Submit transition with the harvest selection in the same payload (§1.4). */
export async function submitBidWithHarvest(
  bidId: string,
  artifactIds: string[],
  options: HarvestRequestOptions = {}
): Promise<void> {
  await api.patch(`/bids/${bidId}/status`, {
    status: 'submitted',
    ...(artifactIds.length > 0
      ? {
          harvest: {
            artifactIds,
            ...(options.confirmRestrictedLibrary ? { confirmRestrictedLibrary: true } : {}),
          },
        }
      : {}),
  })
}

/** Late harvest from the bid overview affordance. */
export async function lateHarvest(
  bidId: string,
  artifactIds: string[],
  options: HarvestRequestOptions = {}
): Promise<{ queued: boolean }> {
  return api.patch(`/bids/${bidId}`, {
    _harvest: {
      artifactIds,
      ...(options.confirmRestrictedLibrary ? { confirmRestrictedLibrary: true } : {}),
    },
  })
}

export interface RecordOutcomeInput {
  outcome: BidOutcomeValue
  score?: number | null
  rank?: number | null
  totalBidders?: number | null
  primaryReason?: PrimaryOutcomeReason | null
  feedbackText?: string | null
  debriefDocumentId?: string | null
}

export async function recordOutcome(
  bidId: string,
  input: RecordOutcomeInput
): Promise<{ outcome: BidOutcome }> {
  return api.patch(`/bids/${bidId}`, { _outcome: input })
}

// ===================
// Unit Brief API (Scoped Brief P1)
//
// Per-section brief (notes + pinned docs, plus a single document's win themes)
// bundled into the outline GET and written via discriminated PATCH ops on
// /artifacts/{id}/outline. Notes, pins and themes share ONE version — every
// successful save returns the latest version, which the store threads into the
// next save to avoid spurious 409s.
// ===================

export interface UnitBriefView {
  sectionKey: string
  notes: string | null
  pinnedDocIds: string[]
  /**
   * A single document's win themes (`__document__` only — an outlined
   * section's are on the outline; see `lib/brief-themes.ts`). Absent from a
   * backend that predates it; read as none.
   */
  themeIds?: string[]
  version: number
  updatedAt: string // P2: anchors stale detection
}

export async function saveBriefNotes(
  artifactId: string,
  sectionKey: string,
  notes: string | null,
  version: number | null,
): Promise<{ unitBrief: UnitBriefView }> {
  return api.patch<{ unitBrief: UnitBriefView }>(`/artifacts/${artifactId}/outline`, {
    op: 'set_brief_notes',
    sectionKey,
    notes,
    version,
  })
}

export async function saveBriefPins(
  artifactId: string,
  sectionKey: string,
  pinnedDocIds: string[],
  version: number | null,
): Promise<{ unitBrief: UnitBriefView }> {
  return api.patch<{ unitBrief: UnitBriefView }>(`/artifacts/${artifactId}/outline`, {
    op: 'set_brief_pins',
    sectionKey,
    pinnedDocIds,
    version,
  })
}

/** Replace a single document's win themes (sectionKey `__document__` only — the server refuses any other). */
export async function saveBriefThemes(
  artifactId: string,
  sectionKey: string,
  themeIds: string[],
  version: number | null,
): Promise<{ unitBrief: UnitBriefView }> {
  return api.patch<{ unitBrief: UnitBriefView }>(`/artifacts/${artifactId}/outline`, {
    op: 'set_brief_themes',
    sectionKey,
    themeIds,
    version,
  })
}

// ===================
// Draft all sections (multi-section response documents)
//
// Rides GET/PATCH /artifacts/{id}/outline as a query view / body op — the
// REST API has no room for a new resource.
// ===================

/**
 * `ready`: empty and the AI may write it for you — drafted from its plan, or
 * planned first when it has none. `not_writable`: the AI may not write it for
 * you (assigned to someone else, you only review it, the document is frozen,
 * or you can't edit the bid) — `skipReason` says which. `has_content`: already
 * has text — never touched.
 */
export type DraftAllSectionState = 'ready' | 'not_writable' | 'has_content'

export interface DraftAllSectionView {
  sectionKey: string
  title: string
  state: DraftAllSectionState
  /** `not_writable` only: the line the dialog shows, e.g. "Skipped Methodology: assigned to Sam Chen." */
  skipReason?: string
}

export interface DraftAllJobView {
  jobId: string
  sessionId: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  progressMessage: string | null
}

export interface DraftAllStatus {
  sections: DraftAllSectionView[]
  /** A draft-all job still running on this document. */
  activeJob: DraftAllJobView | null
}

export async function getDraftAllStatus(artifactId: string): Promise<DraftAllStatus> {
  return api.get<DraftAllStatus>(`/artifacts/${artifactId}/outline`, { draftAll: 'status' })
}

/**
 * Queue one AI job drafting the given empty sections. The server drafts only
 * those still empty and writable when the request lands.
 * 409 `DRAFT_ALL_BUSY` carries `{ jobId, sessionId }` of the job already running;
 * 409 `NOTHING_TO_DRAFT` carries the current `sections`.
 */
export async function startDraftAll(
  artifactId: string,
  sectionKeys: string[]
): Promise<{ jobId: string; sessionId: string; sectionKeys: string[] }> {
  return api.patch(`/artifacts/${artifactId}/outline`, { op: 'draft_all', sectionKeys })
}

// ===================
// Draft document (R1 whole-document job), behind the server's kill switch
//
// Rides the same outline route as Draft all: `?documentJob=status` and the
// `start_document_job` body op. While the switch is off the status says
// `{ enabled: false }` and the page keeps Draft all sections.
// ===================

/**
 * `writable`: the job would draft it for you (`hasText`: it has text, so you
 * would get a suggestion, never a change). `not_writable`: see `skipReason`.
 */
export interface DocumentJobEntrySection {
  sectionKey: string
  title: string
  state: 'writable' | 'not_writable'
  hasText: boolean
  skipReason?: string
}

export type DocumentJobEntryStatus =
  | { enabled: false }
  | {
    enabled: true
    /** `document_row`: a single document drafted as one; `rows`: section by section. */
    shape: 'rows' | 'document_row'
    sections: DocumentJobEntrySection[]
    /** A waiting or running document job on this document. */
    activeJobId: string | null
  }

export async function getDocumentJobEntryStatus(artifactId: string): Promise<DocumentJobEntryStatus> {
  return api.get<DocumentJobEntryStatus>(`/artifacts/${artifactId}/outline`, { documentJob: 'status' })
}

/**
 * Queue the whole-document job over the given sections (the writable ones the
 * dialog offered). 409 `DOCUMENT_JOB_BUSY` carries `{ jobId }` of the job already
 * running; 409 `NOTHING_TO_DRAFT` carries `{ skipped }`; 503
 * `DOCUMENT_JOB_UNAVAILABLE` and 403 carry a plain message.
 */
export async function startDocumentJob(
  artifactId: string,
  sectionKeys: string[]
): Promise<{
  jobId: string
  targets: Array<{ sectionKey: string; heading: string }>
  skipped: Array<{ sectionKey: string; heading: string; reason: string }>
}> {
  return api.patch(`/artifacts/${artifactId}/outline`, { op: 'start_document_job', sectionKeys })
}

/**
 * Get artifact outline
 */
export async function getArtifactOutline(
  artifactId: string
): Promise<{ outline: Outline; outlineVersion: number; unitBriefs?: UnitBriefView[] }> {
  return api.get<{ outline: Outline; outlineVersion: number; unitBriefs?: UnitBriefView[] }>(
    `/artifacts/${artifactId}/outline`
  )
}

/**
 * Update artifact outline.
 *
 * `expectedOutlineVersion` is the optimistic version check: the write lands only
 * while the server's `outline_version` still matches, otherwise it 409s rather
 * than clobbering a concurrent writer. Callers must handle the 409 by refetching
 * and rebasing (drafting-store does) — never by dropping the edit.
 */
export async function updateArtifactOutline(
  artifactId: string,
  outline: Outline,
  expectedOutlineVersion?: number
): Promise<{ outline: Outline; outlineVersion: number }> {
  return api.patch<{ outline: Outline; outlineVersion: number }>(
    `/artifacts/${artifactId}/outline`,
    expectedOutlineVersion === undefined ? { outline } : { outline, expectedOutlineVersion }
  )
}

// ===================
// Draft annotation report (spec 19)
//
// What author-only annotations are still sitting in this artifact's draft, so
// the pre-export dialog can tell the author what export is about to strip — and
// what it CANNOT strip (see `malformedCount`).
// ===================

export interface AnnotationReportSection {
  /**
   * The OUTLINE section key, not `segment.sectionId`. Single-document artifacts
   * report under the literal key `__document__`, which has no outline entry —
   * callers need a fallback label. See `mapAnnotationReport` in
   * `@/lib/exportGaps`.
   */
  sectionId: string
  /**
   * Single documents only: the section heading these gaps sit under. One
   * `sectionId` may arrive several times, once per heading. Absent before the
   * first heading and for outline documents — fall back as for `sectionId`.
   */
  title?: string
  /** TODO note text, in document order. An empty payload arrives as ''. */
  todos: string[]
  attachmentCount?: number
  /** Unclosed tokens. Omitted when zero. These SURVIVE export, verbatim. */
  malformedCount?: number
}

export interface AnnotationReport {
  /** Alphabetical by section key — sort against the outline before rendering. */
  sections: AnnotationReportSection[]
  totalTodos: number
  hasMalformed: boolean
}

export async function getArtifactAnnotations(artifactId: string): Promise<AnnotationReport> {
  return api.get<AnnotationReport>(`/artifacts/${artifactId}/annotations`)
}

/**
 * Get section status for an artifact
 */
export async function getArtifactSectionStatus(
  artifactId: string
): Promise<{ sections: SectionStatus[] }> {
  return api.get<{ sections: SectionStatus[] }>(`/artifacts/${artifactId}/status`)
}

/** One drafted segment behind a Full response section: its citation refs. */
export interface FullResponseSegment {
  segmentId: string
  citationRefs: CitationRef[]
  libraryCitationRefs: CitationRef[]
}

/** One section of the assembled Full response (spec 29 §8). */
export interface FullResponseSection {
  sectionKey: string
  title: string
  wordCount: number
  /**
   * Drafted HTML, '' when nothing has been drafted. Each segment's part is
   * wrapped in `<div data-segment-id="…">` — citation numbers restart per
   * segment, so a marker resolves against its own segment's refs.
   */
  html: string
  /** The export's view of the same HTML: citation markers and author annotations stripped. */
  evaluatorHtml: string
  updatedAt: string | null
  /**
   * The segments behind `html`, in order, with their citation refs. Optional:
   * a backend that predates reader citations omits it, and the reader then
   * shows plain markers and no sources count.
   */
  segments?: FullResponseSegment[]
}

export interface FullResponseDocument {
  artifactId: string
  outlineVersion: number
  totalWords: number
  sections: FullResponseSection[]
}

/** The assembled response, every outline section in order — read-only. */
export async function getArtifactDocument(artifactId: string): Promise<FullResponseDocument> {
  return api.get<FullResponseDocument>(`/artifacts/${artifactId}/document`)
}

/**
 * A section whose AI change still awaits Accept/Reject, so the exported file
 * carries its last accepted text — or, with none, leaves it out (R0-A).
 */
export interface PendingAiChangeNotice {
  sectionName: string
  hasAcceptedVersion: boolean
}

type ExportStatusResponse =
  | { status: 'running' }
  | {
      status: 'complete'
      downloadUrl: string
      filename: string
      sizeBytes: number
      /** Absent from a backend older than R0-A. */
      pendingAiChanges?: PendingAiChangeNotice[]
      /** The retained Word version (whole-document exports, R3). */
      outputVersionId?: string
      /** The PDF converted from it, when a PDF was asked for (R3). */
      pdf?: OutputVersionWithDownload
    }
  | { status: 'failed'; code: string; message: string }

type CompleteExport = Extract<ExportStatusResponse, { status: 'complete' }>

const EXPORT_POLL_INTERVAL_MS = 2_000
const EXPORT_POLL_TIMEOUT_MS = 3 * 60_000

export interface ExportOptions {
  /**
   * The user confirmed the template's own pages may be replaced (D4). Without
   * it, a template with no content placeholder is refused with a 409
   * `TEMPLATE_CONTENT_LOSS` carrying the template check report.
   */
  acknowledgeTemplateLoss?: boolean
  /** Word (the default) or PDF (R3). The template check applies to both. */
  format?: ExportFormat
}

export type ExportFormat = 'docx' | 'pdf'

/** Start the browser download of a presigned link. */
export function downloadFromUrl(url: string, filename = ''): void {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** POST the export, then poll its status until the render leg answers. */
async function startAndPollExport(
  artifactId: string,
  body: Record<string, unknown>,
  /** Called once the server has accepted the export (before polling). */
  onAccepted?: () => void,
): Promise<CompleteExport> {
  const { exportId } = await api.post<{ exportId: string }>(`/artifacts/${artifactId}/export`, body)
  onAccepted?.()

  const deadline = Date.now() + EXPORT_POLL_TIMEOUT_MS
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_INTERVAL_MS))

    const result = await api.get<ExportStatusResponse>(
      `/artifacts/${artifactId}/export/status?exportId=${exportId}`,
    )

    if (result.status === 'complete') return result

    if (result.status === 'failed') {
      throw new ApiRequestError({ code: result.code, message: result.message })
    }

    if (Date.now() >= deadline) {
      throw new ApiRequestError({
        code: 'EXPORT_TIMEOUT',
        message: 'Export is taking longer than expected. Please try again.',
      })
    }
  }
}

/**
 * Export artifact to Word document (.docx).
 *
 * Async start + poll (production-hardening H7): the render runs in the
 * background with no API Gateway 29s cap; this resolves once the browser
 * download has been triggered, so callers' existing loading states just
 * cover a (potentially) longer await.
 *
 * Resolves with the sections that went out with their last accepted text
 * because an AI change still awaits Accept/Reject, so the caller can say so.
 */
export async function exportArtifactToDocx(
  artifactId: string,
  sectionKey?: string,
  templateId?: string,
  options: ExportOptions = {},
): Promise<{ pendingAiChanges: PendingAiChangeNotice[] }> {
  const result = await startAndPollExport(artifactId, {
    format: 'docx',
    ...(sectionKey ? { sectionKey } : {}),
    ...(templateId ? { templateId } : {}),
    ...(templateId && options.acknowledgeTemplateLoss ? { acknowledgeTemplateLoss: true } : {}),
  })
  downloadFromUrl(result.downloadUrl, result.filename)
  return { pendingAiChanges: result.pendingAiChanges ?? [] }
}

/**
 * Start a PDF export of the whole document (R3). Resolves once the Word file
 * it is converted from exists, with the PDF output version — usually still
 * `pending`; poll it with {@link getOutputVersion}. Downloads nothing: the
 * caller downloads the PDF once it is ready.
 */
export async function exportArtifactToPdf(
  artifactId: string,
  templateId?: string,
  options: ExportOptions = {},
  /**
   * Called once the server accepts the export. A refusal (the template
   * check's 409, D4) never calls it, so no "Making PDF…" flashes before the
   * warning.
   */
  onAccepted?: () => void,
): Promise<{ pendingAiChanges: PendingAiChangeNotice[]; pdf: OutputVersionWithDownload; filename: string }> {
  const result = await startAndPollExport(
    artifactId,
    {
      format: 'pdf',
      ...(templateId ? { templateId } : {}),
      ...(templateId && options.acknowledgeTemplateLoss ? { acknowledgeTemplateLoss: true } : {}),
    },
    onAccepted,
  )
  if (!result.pdf) {
    throw new ApiRequestError({ code: 'PDF_UNAVAILABLE', message: "The PDF couldn't be started. Please try again." })
  }
  return {
    pendingAiChanges: result.pendingAiChanges ?? [],
    pdf: result.pdf,
    filename: result.filename.replace(/\.docx$/i, '.pdf'),
  }
}

/**
 * Turn an existing Word version of this document into a PDF — a retry tied to
 * the same Word file, or a completed buyer form's "PDF preview" (pass the
 * `form_docx` id from the form export). Returns the PDF version: pending, or
 * the ready PDF of that Word file if one already exists.
 */
export async function convertOutputToPdf(
  artifactId: string,
  sourceOutputId: string,
): Promise<OutputVersionWithDownload> {
  const { outputVersion } = await api.post<{ outputVersion: OutputVersionWithDownload }>(
    `/artifacts/${artifactId}/export`,
    { format: 'pdf', sourceOutputId },
  )
  return outputVersion
}

/** One output version of this document, with a fresh download link once it is ready. */
export async function getOutputVersion(
  artifactId: string,
  outputId: string,
): Promise<OutputVersionWithDownload> {
  const { outputVersion } = await api.get<{ outputVersion: OutputVersionWithDownload }>(
    `/artifacts/${artifactId}/export/status?outputId=${outputId}`,
  )
  return outputVersion
}

// ===================
// Export Templates API
// ===================

/** How much template content a part of the check measured (backend `docx-template-check.ts`). */
export interface TemplateContentMeasure {
  words: number
  tables: number
  images: number
  pageBreaks: number
  sectionBreaks: number
  /** "About" this many pages — an estimate. */
  estimatedPages: number
}

/** The template check report — run on upload and at export (R3 T1). */
export interface TemplateCheckReport {
  version: number
  placeholder: {
    found: boolean
    kind: 'bookmark' | 'text' | null
    usable: boolean
    problem: 'in_table' | 'in_text_box' | 'mid_sentence' | null
    /** A misplaced typed marker: stays on the first page as typed, or goes with the replaced pages. Absent before check v3. */
    typedMarker?: 'kept' | 'removed' | null
  }
  responseGoes: 'at_placeholder' | 'after_first_break' | 'whole_body'
  kept: {
    before: TemplateContentMeasure | null
    after: TemplateContentMeasure | null
    headers: boolean
    footers: boolean
  }
  /** Template content the response replaces; null when nothing is lost. */
  replaced: TemplateContentMeasure | null
  /** Why the export asks first: no placeholder, or one placed where it takes template text with it. */
  replacedReason: 'no_placeholder' | 'placeholder_unsafe' | null
  tableOfContents: boolean
  multiColumnResponse: boolean
  pdfSubstitutedFonts: string[]
  /** Exporting replaces part of the template, so the user must confirm (D4). */
  needsAcknowledgement: boolean
}

export interface ExportTemplate {
  id: string
  name: string
  originalFilename: string
  s3Key: string
  fileSizeBytes: number
  isDefault: boolean
  hasPlaceholder: boolean
  previewS3Key: string | null
  previewStatus: 'pending' | 'processing' | 'ready' | 'failed' | null
  /** Null for a template uploaded before the check existed (absent from older backends). */
  checkReport?: TemplateCheckReport | null
  createdBy: string
  createdAt: string
  updatedAt: string
  organizationId: string
}

interface ExportTemplateUploadUrlResponse {
  uploadUrl: string
  s3Key: string
  templateId: string
}

export async function listExportTemplates(): Promise<ExportTemplate[]> {
  return api.get<ExportTemplate[]>('/export-templates')
}

export async function requestExportTemplateUploadUrl(
  filename: string,
  fileSize: number,
): Promise<ExportTemplateUploadUrlResponse> {
  return api.post<ExportTemplateUploadUrlResponse>('/export-templates/upload-url', {
    filename,
    fileSize,
  })
}

export async function createExportTemplate(data: {
  templateId: string
  name: string
  originalFilename: string
  fileSizeBytes: number
}): Promise<ExportTemplate> {
  return api.post<ExportTemplate>('/export-templates', data)
}

export async function renameExportTemplate(
  id: string,
  name: string,
): Promise<ExportTemplate> {
  return api.patch<ExportTemplate>(`/export-templates/${id}`, { name })
}

export async function deleteExportTemplate(id: string): Promise<void> {
  await api.delete(`/export-templates/${id}`)
}

export async function setDefaultExportTemplate(id: string): Promise<ExportTemplate> {
  return api.patch<ExportTemplate>(`/export-templates/${id}/default`, {})
}

export async function getExportTemplateDownloadUrl(
  id: string,
): Promise<{ downloadUrl: string; filename: string }> {
  return api.get<{ downloadUrl: string; filename: string }>(`/export-templates/${id}/download-url`)
}

export async function getExportTemplatePreviewUrl(
  id: string,
): Promise<{ previewUrl: string | null; status: string }> {
  return api.get<{ previewUrl: string | null; status: string }>(`/export-templates/${id}/preview-url`)
}

// ===================
// Outline Templates API
// ===================

export interface OutlineTemplateSection {
  title: string
  description?: string
  targetWordCount?: number
}

export interface OutlineTemplate {
  id: string
  organizationId: string
  name: string
  description: string | null
  sections: OutlineTemplateSection[]
  visibility: 'private' | 'shared'
  createdBy: string
  creatorName: string
  createdAt: string
  updatedAt: string
}

export async function listOutlineTemplates(): Promise<OutlineTemplate[]> {
  const data = await api.get<{ templates: OutlineTemplate[] }>('/outline-templates')
  return data.templates
}

export async function getOutlineTemplate(id: string): Promise<OutlineTemplate> {
  return api.get<OutlineTemplate>(`/outline-templates/${id}`)
}

export async function createOutlineTemplate(data: {
  name: string
  description?: string
  sections: OutlineTemplateSection[]
  visibility?: 'private' | 'shared'
}): Promise<OutlineTemplate> {
  return api.post<OutlineTemplate>('/outline-templates', data)
}

export async function updateOutlineTemplate(
  id: string,
  data: {
    name?: string
    description?: string | null
    sections?: OutlineTemplateSection[]
    visibility?: 'private' | 'shared'
  },
): Promise<OutlineTemplate> {
  return api.patch<OutlineTemplate>(`/outline-templates/${id}`, data)
}

export async function deleteOutlineTemplate(id: string): Promise<void> {
  await api.delete(`/outline-templates/${id}`)
}

export async function duplicateOutlineTemplate(id: string): Promise<OutlineTemplate> {
  return api.post<OutlineTemplate>(`/outline-templates/${id}/duplicate`, {})
}

// ===================
// Questionnaire API
// ===================

import type {
  QuestionnaireItem,
  AddQuestionInput,
  UpdateQuestionInput,
  ParsedQuestion,
} from '@/types/questionnaire-types'

/**
 * Add questions to a questionnaire artifact
 */
export async function addQuestions(
  artifactId: string,
  questions: AddQuestionInput[]
): Promise<{ questions: QuestionnaireItem[]; totalQuestions: number; outlineVersion: number }> {
  return api.post(`/artifacts/${artifactId}/questions`, { questions })
}

/**
 * Update a single question in a questionnaire
 */
export async function updateQuestion(
  artifactId: string,
  sectionKey: string,
  updates: UpdateQuestionInput
): Promise<{ question: QuestionnaireItem; outlineVersion: number }> {
  return api.patch(`/artifacts/${artifactId}/questions/${sectionKey}`, updates)
}

/**
 * Delete a question from a questionnaire
 */
export async function deleteQuestion(
  artifactId: string,
  sectionKey: string
): Promise<void> {
  return api.delete(`/artifacts/${artifactId}/questions/${sectionKey}`)
}

/**
 * Parse pasted text into structured questions using AI
 */
export async function parseQuestions(
  artifactId: string,
  text: string
): Promise<{ parsed: ParsedQuestion[]; warnings: string[] }> {
  return api.post(`/artifacts/${artifactId}/parse-questions`, { text })
}

/**
 * Compile context pack for a section
 */
export interface ContextPack {
  artifactContext: {
    artifactId: string
    artifactName: string
  }
  sectionContext: {
    sectionKey: string
    title: string
    order: number
    targetWordCount: number
  }
  selectedEvidence: string[]
  tenderContext: {
    bidTitle: string
    client: string
    industry: string
    deadline: string | null
  }
  relevantRequirements: Array<{
    id: string
    text: string
    type: 'mandatory' | 'desirable' | 'information'
    category: string | null
    mappingConfidence: number
  }>
  relevantCriteria: Array<{
    id: string
    title: string
    weight: number | null
    description: string | null
  }>
  evidenceCandidates: Array<{
    documentId: string
    chunkIndex: number
    documentName: string
    snippet: string
    pageNumber: number | null
    relevanceScore: number
    highlightData: unknown
    source: 'tender' | 'library' | 'past_bid'
  }>
  tokenEstimate: {
    evidenceTokens: number
    requirementsTokens: number
    criteriaTokens: number
    metadataTokens: number
    totalTokens: number
    budgetLimit: number
    withinBudget: boolean
  }
}

export interface PreflightWarning {
  type: string
  passed: boolean
  severity: string
  message: string
  details?: Record<string, unknown>
}

export async function compileContextPack(
  artifactId: string,
  sectionKey: string,
  selectedEvidence?: string[]
): Promise<{
  contextPack: ContextPack
  preflight: { canProceed: boolean; warnings: PreflightWarning[] }
}> {
  return api.post<{
    contextPack: ContextPack
    preflight: { canProceed: boolean; warnings: PreflightWarning[] }
  }>(`/artifacts/${artifactId}/sections/${sectionKey}/context`, {
    selectedEvidence,
  })
}

/**
 * Get drafting session for a section
 */
export interface DraftingSession {
  id: string
  organizationId: string
  bidId: string
  artifactId: string
  sectionKey: string
  status: string
  currentPlanId: string | null
  draftContent: string | null
  draftVersion: number
  sources: unknown[] | null
  finalContent: string | null
  contextPack: ContextPack | null
  verifierOutput: unknown
  autoFixAttempts: number
  errorMessage: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  completedAt: string | null
}

export async function getDraftingSession(
  artifactId: string,
  sectionKey: string
): Promise<{ session: DraftingSession | null; status: string }> {
  return api.get<{ session: DraftingSession | null; status: string }>(
    `/artifacts/${artifactId}/sections/${sectionKey}/session`
  )
}

// ===================
// Response Plans API (Phase 1B)
// ===================

// ===================
// Live plan model (Model B) — what set_plan emits and the panel renders (Scoped Brief P2)
// ===================

export interface PlanResolvedEvidenceItem {
  documentId: string
  documentName: string
  confidenceBand: 'high' | 'medium' | 'low'
  pinned: boolean
}

// ─── One plan format (mirrors backend `plan-patch-types.ts`, technical design §3.5) ──

export type PlanChecklistKind =
  | 'requirement' | 'criterion' | 'brief' | 'theme' | 'instruction' | 'limit'

/**
 * One thing a section must do. `ref` is a compliance item id (`requirement`) or
 * an evaluation criterion id (`criterion`), validated by the server; never
 * render it raw — show the REQ label or the item `text`.
 */
export interface PlanChecklistItem {
  kind: PlanChecklistKind
  ref?: string
  text: string
}

/** Where a section's word target came from. There is no default length. */
export type PlanLengthBasis = 'tender_limit' | 'weighting' | 'past_proposal' | 'user' | 'judgement'

export type PlanFactKind =
  | 'buyer' | 'project' | 'stage' | 'numbering' | 'team_member' | 'date' | 'figure' | 'price_status'

/** `to_confirm` and `question_raised` are both unconfirmed; the latter has a durable question. */
export type PlanFactStatus = 'sourced' | 'to_confirm' | 'question_raised'

export interface PlanFact {
  key: string
  kind: PlanFactKind
  /** What the writers read; may end with an instruction for them. Shown only when `display` is absent. */
  value: string
  /** The plan card's line for this fact, when the server set one (UX2). */
  display?: string
  status: PlanFactStatus
  sourceRef?: {
    kind: 'tender' | 'library' | 'memory' | 'company_profile' | 'user'
    id: string
    chunkId?: string
    page?: number
  }
  questionId?: string
}

export interface LivePlanSection {
  sectionId: string
  heading: string
  targetWordCount: number
  keyPoints: string[]
  evidenceHints?: string[]
  // Document-job fields — all optional (plans stored before them have none).
  // The card shows purpose, checklist and lengthBasis read-only and sends every
  // one back unchanged; the server keeps them (it drops checklist refs it never
  // issued and records `lengthBasis: 'user'` when the word target changes).
  purpose?: string
  checklist?: PlanChecklistItem[]
  lengthBasis?: PlanLengthBasis
  focus?: string
  dependsOn?: string[]
  passageIds?: string[]
  exemplarRefs?: string[]
}

// ─── Rich planner payload (mirrors backend `plan-patch-types.ts:90-178`) ──────

/** A citation reference to evidence supporting a planned claim. */
export interface PlanCitationRef {
  documentId: string
  chunkIndex: number
  documentName: string
  pageNumber: number | null
}

/** Maps a claim/point to the evidence supporting it. */
export interface PlanEvidenceMapItem {
  point: string
  citations: PlanCitationRef[]
}

export type PlanProofPointType =
  | 'metric' | 'project' | 'certification' | 'team' | 'temporal' | 'standard'

/** Concrete data the drafter must include verbatim. */
export interface PlanProofPoint {
  type: PlanProofPointType
  value: string
  /** The section heading this belongs in — remapped server-side on every write. */
  inHeading: string
  /** 1-based index into the drafter's evidence candidates. */
  citationIndex?: number
}

/** The implication-for-this-client behind a capability claim. */
export interface PlanSoWhatItem {
  claim: string
  implication: string
  /** Same `inHeading` invariant as `PlanProofPoint`. */
  inHeading: string
}

export type PlanOrgInclusionType = 'capability' | 'certification' | 'project'

/** An organisation asset the response should include. */
export interface PlanOrgInclusion {
  type: PlanOrgInclusionType
  name: string
  /** Free text ("where used") — NOT a heading reference, so it is never remapped. */
  whereUsed: string
}

export interface LivePlanJson {
  goal: string
  sections: LivePlanSection[]
  /**
   * requirementId → sectionId[]. Optional here on purpose: a stored plan is
   * not guaranteed to carry one, so every reader guards it (`?? {}`).
   */
  coverageMap?: Record<string, string[]>
  resolvedEvidence?: PlanResolvedEvidenceItem[]
  briefSnapshotAt?: string | null

  // ─── Document-job plan fields (server-owned) ──────────────────
  // Same guarantee as the rich payload below: stripped from a save and carried
  // forward from the stored plan by `buildEditedPlan`.
  /** The facts sheet; the card lists the unconfirmed ones under "Facts to confirm". */
  facts?: PlanFact[]
  voiceSample?: { passageId: string }
  assumptions?: string[]

  // ─── Rich planner payload (3F-a §5.4) ─────────────────────────
  // Server-owned and SERVER-GUARANTEED: `UpdateLivePlanContentSchema` is a
  // non-strict `z.object({ goal, sections })` (`backend/src/lib/schemas.ts`),
  // so unknown keys are dropped on save and `buildEditedPlan` carries the stored
  // values forward. These are typed here so the card can READ them without a
  // cast — the type is not what stops a client authoring them, the backend is.
  // (No `Omit` at `updatePlan`: `PlanEditDraft.base` is the full stored plan and
  // saves spread it — see `src/lib/plan-edit.ts:82-86`.)
  //
  // All optional and all tolerant of absence: plans persisted before these
  // existed, and conversational `set_plan` plans the model chose not to enrich,
  // legitimately have none. Readers MUST treat undefined as empty.
  /** What carries the most marks in this section per the criteria weighting. */
  evaluatorFocus?: string
  /** How this section positions against likely competitors. */
  differentiationAngle?: string
  evidenceMap?: PlanEvidenceMapItem[]
  proofPoints?: PlanProofPoint[]
  soWhat?: PlanSoWhatItem[]
  orgInclusions?: PlanOrgInclusion[]
  openQuestions?: string[]
  riskFlags?: string[]
  /** Non-fatal findings from the plan validators — diagnostics, never a gate. */
  validationWarnings?: string[]
}

// ─── Plan Suggestion (§4.1/§5) ──────────────────────────────────

export type PlanSuggestionKind = 'requirement' | 'criterion' | 'theme' | 'document'

/**
 * Mirror of the backend `PlanSuggestion` wire shape (plan-patch-types.ts).
 * Review-time only — NEVER persisted to plan_json (§4.1). Suggestions ride the
 * `plan_patch_applied` event and are held in frontend Zustand state only.
 */
export interface PlanSuggestion {
  id: string
  kind: PlanSuggestionKind
  /** Human-readable label (requirement/criterion/theme title, or document name). */
  title: string
  /** One-line "why" shown inline (§4.4 reasoning made scannable). */
  reason: string
  /** Stakes rank — lower = higher priority (§5.1: req=0, criterion=1, theme=2, doc=3). */
  rank: number
  /** Fold-into-brief payload. Exactly one identity field is set per kind. */
  requirementId?: string
  requirementKind?: 'compliance' | 'criterion'
  themeId?: string
  documentId?: string
  /** The unit this fires for (where Accept links/pins). */
  artifactId: string
  sectionKey: string
}

export interface ResponsePlan {
  id: string
  organizationId: string
  bidId: string
  artifactId: string
  sectionKey: string
  /**
   * The persisted `plan_json`. `LivePlanJson` now TYPES the server-owned rich
   * fields (`proofPoints`, `soWhat`, `evidenceMap`, `orgInclusions`,
   * `evaluatorFocus`, `differentiationAngle`, `openQuestions`, `riskFlags`,
   * `validationWarnings`) so the card can read them without a cast (3F-a §5.4).
   *
   * **The guarantee that no client authors them is the BACKEND's, not the
   * type's**: `UpdateLivePlanContentSchema` is a non-strict
   * `z.object({ goal, sections })` (`backend/src/lib/schemas.ts:876-884`), so
   * unknown keys are dropped, and `buildEditedPlan` rebuilds from
   * `edit.goal`/`edit.sections` + the stored plan (`handlers/plans.ts:270-307`).
   * The client does send them — `PlanEditDraft.base` is the full stored plan and
   * saves spread it.
   *
   * Always rebuild an edited plan by spreading this object, never by
   * constructing a literal — see `src/lib/plan-edit.ts`.
   */
  planJson: LivePlanJson
  version: number
  status: 'draft' | 'approved' | 'rejected'
  userEdits: unknown[]
  createdBy: string
  createdAt: string
  approvedAt: string | null
  approvedBy: string | null
}

/** Audit-trail entry appended by `PATCH /plans/{planId}`. Nothing reads it back. */
export interface PlanUserEdit {
  field: string
  oldValue: string
  newValue: string
  /** ISO-8601 datetime. */
  timestamp: string
}

/** `details` of the 409 returned when the plan moved under an open card. */
export interface PlanConflictDetails {
  error: 'plan_conflict'
  currentVersion: number
  requestedBaseVersion: number
}

/** `details` of the 400 returned when an edit would break a plan invariant. */
export interface PlanInvariantDetails {
  error: 'invariant_violation'
  conflictingSectionIds?: string[]
}

export interface UpdatePlanResponse {
  plan: ResponsePlan
  /** Draft segments this edit flipped to `stale`. May be empty. */
  staleSectionIds: string[]
}

export async function getPlanById(planId: string): Promise<{ plan: ResponsePlan }> {
  return api.get<{ plan: ResponsePlan }>(`/plans/${planId}`)
}

/**
 * Update a draft plan.
 *
 * `planJson` is the WHOLE edited plan, not a diff — omitting a section REMOVES
 * it (and prunes its coverage), so never send a filtered or paginated subset.
 * Typed as the full `LivePlanJson` on purpose: it makes a hand-built
 * `{ goal, sections }` literal a type error, which is the shape that silently
 * strips the rich fields.
 *
 * `expectedVersion` is the `version` of the plan that was rendered. A mismatch
 * returns 409 with `PlanConflictDetails` and saves nothing.
 *
 * Server-owned keys (`coverageMap`, `proofPoints`, `sectionType`, …) are
 * stripped by the request schema and carried forward from the stored plan, so
 * sending the spread object is harmless. `evidenceHints` omitted keeps the
 * stored list; `evidenceHints: []` clears it.
 */
export async function updatePlan(
  planId: string,
  data: {
    planJson: LivePlanJson
    expectedVersion: number
    userEdits?: PlanUserEdit[]
  }
): Promise<UpdatePlanResponse> {
  return api.patch<UpdatePlanResponse>(`/plans/${planId}`, data)
}

/**
 * Approve a draft plan
 */
export async function approvePlan(planId: string): Promise<{ plan: ResponsePlan }> {
  return api.post<{ plan: ResponsePlan }>(`/plans/${planId}/approve`, {})
}

/**
 * Reject a draft plan
 */
export async function rejectPlan(planId: string): Promise<{ plan: ResponsePlan }> {
  return api.post<{ plan: ResponsePlan }>(`/plans/${planId}/reject`, {})
}

/**
 * Get plan history for a section
 */
export async function getPlanHistory(
  artifactId: string,
  sectionKey: string
): Promise<{ plans: ResponsePlan[] }> {
  return api.get<{ plans: ResponsePlan[] }>(
    `/artifacts/${artifactId}/sections/${sectionKey}/plans`
  )
}

// ===================
// Collaborative Planning API (Phase 1D)
// ===================

export interface PlanningMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  status?: 'complete' | 'stopped'
  planSnapshot?: PlanDraft
  planStatus?: 'discussing' | 'proposed' | 'approved'
  userIntent?: 'question' | 'feedback' | 'approval' | 'unclear'
}

export interface PlanDraft {
  goal: string
  sections: Array<{
    heading: string
    wordCount: number
    keyPoints: string[]
    evidenceSources?: string[]
    requirementsAddressed?: string[]
  }>
  gaps?: string[]
}

export type PlanningStatus = 'discussing' | 'proposed' | 'approved'

export interface PlanningSession {
  planId: string
  status: PlanningStatus
  messages: PlanningMessage[]
  currentPlan: PlanDraft | null
  sessionId: string | null
}




// ===================
// Drafting API (Phase 1C)
// ===================

/**
 * Verifier output types
 */
export type VerifierStatus = 'PASS' | 'AUTO_FIX' | 'NEEDS_USER_REVIEW'

export type IssueType = 'coverage' | 'grounding' | 'citation' | 'risk' | 'style'

export type IssueSeverity = 'critical' | 'warning' | 'info'

export interface VerifierIssue {
  type: IssueType
  severity: IssueSeverity
  message: string
  autoFixable: boolean
  suggestedFix?: string
  fixInstructions?: string
  location?: {
    paragraphIndex: number
    startOffset?: number
    endOffset?: number
  }
}

export interface VerifierMetrics {
  wordCount: number
  targetWordCount: number
  wordCountDeviation: number
  citationCount: number
  paragraphCount: number
  headingsFound: string[]
  uncoveredRequirements: string[]
  orphanedSources: number[]
}

export interface VerifierOutput {
  status: VerifierStatus
  issues: VerifierIssue[]
  summary: string
  metrics: VerifierMetrics
}





export interface EditSelectionResponse {
  originalText: string
  replacementText: string
  diff: {
    type: 'unified'
    lines: Array<{
      type: 'removed' | 'added' | 'unchanged'
      content: string
    }>
  }
  selectionRange: {
    from: number
    to: number
  }
  promptVersion: string
}








/**
 * Edit selected text with AI
 */
export async function editSelection(
  sessionId: string,
  data: {
    selectedText: string
    selectionRange: { from: number; to: number }
    instruction: string
    selectedEvidence?: string[] | undefined
    attachments?: ChatAttachmentRef[] | undefined
    factBankOnly?: boolean | undefined
  }
): Promise<EditSelectionResponse> {
  return api.post<EditSelectionResponse>(`/agent/sessions/${sessionId}/edit-selection`, data)
}

export interface ClassifySelectionIntentResponse {
  intent: 'edit' | 'ask'
}

/**
 * Classify whether a user instruction about a selection is an edit request
 * or a question. Used to route between the rewrite endpoint and chat.
 */
export async function classifySelectionIntent(data: {
  selectedText: string
  instruction: string
}): Promise<ClassifySelectionIntentResponse> {
  return api.post<ClassifySelectionIntentResponse>(
    '/agent/classify-selection-intent',
    data,
  )
}

/** What a server-side reject did to the section (R0-A). */
export interface ProposalRejectionResult {
  decision: 'rejected'
  /** False when the section already held the text from before the change. */
  restored: boolean
  /** The section as it now stands; null when it no longer exists. */
  segment: {
    id: string
    sectionId: string
    heading: string
    version: number
    status: DraftSegment['status']
    wordCount: number
    content: string
  } | null
}

/**
 * Record a user accept/reject decision on an AI-proposed draft patch.
 * The backend uses this to filter the proposal from the LLM's conversation
 * memory on rejection — without it, subsequent turns reason as if the change
 * had been applied.
 *
 * Reject also puts the section's earlier text back on the server (R0-A) and
 * resolves with the section as it now stands. It rejects with a 409
 * (`PROPOSAL_SECTION_CHANGED`) when the section changed after the AI write —
 * nothing is overwritten then. An older backend answers 204 without restoring;
 * that resolves to null and the caller must restore the text itself.
 */
export async function resolveProposal(
  sessionId: string,
  proposalId: string,
  decision: 'accepted' | 'rejected',
): Promise<ProposalRejectionResult | null> {
  const result = await apiRequest<ProposalRejectionResult | undefined>(
    `/agent/sessions/${sessionId}/proposals/${proposalId}/resolution`,
    { method: 'POST', body: { decision }, silentStatuses: [409] },
  )
  return result ?? null
}

/** The section as it stands after a staged decision or an Undo (R1 W1). */
export interface ResolvedSegmentSnapshot {
  id: string
  sectionId: string
  heading: string
  version: number
  status: DraftSegment['status']
  wordCount: number
  content: string
}

/** What deciding a staged suggestion did (R1 W1). */
export type StagedSuggestionDecisionResult =
  | { decision: 'accepted'; segment: ResolvedSegmentSnapshot | null }
  | { decision: 'rejected'; restored: false; segment: ResolvedSegmentSnapshot | null }

/**
 * Accept or reject a STAGED suggestion (R1 W1). Accept applies the suggestion
 * — or `mergedHtml`, the user's per-hunk merge over the current text — only if
 * the section has not moved since the suggestion was made; otherwise it
 * rejects with 409 `SUGGESTION_OUTDATED` (`details.currentVersion`,
 * `details.currentContent`) and nothing changes. Reject writes nothing.
 * Legacy (applied) proposals keep using `resolveProposal`.
 */
export async function resolveStagedSuggestion(
  sessionId: string,
  proposalId: string,
  decision: 'accepted' | 'rejected',
  mergedHtml?: string,
): Promise<StagedSuggestionDecisionResult | null> {
  const body = mergedHtml !== undefined && decision === 'accepted'
    ? { decision, mergedHtml: stripDiffMarkup(mergedHtml) }
    : { decision }
  const result = await apiRequest<StagedSuggestionDecisionResult | undefined>(
    `/agent/sessions/${sessionId}/proposals/${proposalId}/resolution`,
    { method: 'POST', body, silentStatuses: [404, 409] },
  )
  return result ?? null
}

/**
 * Undo an AI edit the user asked for (R1 W1). Puts back the text from before
 * the edit, only while the section still holds the edit; otherwise it rejects
 * with 409 `PROPOSAL_SECTION_CHANGED` and nothing changes.
 */
export async function undoAiEdit(
  sessionId: string,
  sectionId: string,
  proposalId: string,
): Promise<{ undone: true; segment: ResolvedSegmentSnapshot | null }> {
  return apiRequest<{ undone: true; segment: ResolvedSegmentSnapshot | null }>(
    `/agent/sessions/${sessionId}/segments/${sectionId}`,
    { method: 'PATCH', body: { action: 'undo_ai_edit', proposalId }, silentStatuses: [404, 409] },
  )
}

// ===================
// Settings API
// ===================

/**
 * Get organization settings (profile + writing preferences)
 */
export async function getOrganizationSettings(): Promise<{ organization: OrgSettings }> {
  return api.get<{ organization: OrgSettings }>('/settings/organization')
}

/**
 * Update company profile (merge - only sends changed fields)
 */
export async function updateOrgProfile(profile: Partial<OrgProfile>): Promise<{ organization: OrgSettings }> {
  return api.patch<{ organization: OrgSettings }>('/settings/profile', profile)
}

/**
 * Update writing preferences (merge - only sends changed fields)
 */
export async function updateWritingPreferences(prefs: Partial<WritingPreferences>): Promise<{ organization: OrgSettings }> {
  return api.patch<{ organization: OrgSettings }>('/settings/writing-preferences', prefs)
}

// ===================
// User Management API
// ===================

export type OrgRole = 'admin' | 'member' | 'viewer'

export interface OrganizationUser {
  id: string
  email: string
  fullName: string | null
  avatarUrl: string | null
  role: OrgRole
  createdAt: string
}

export interface InvitationSummary {
  id: string
  email: string
  role: OrgRole
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  expiresAt: string
  createdAt: string
  acceptedAt: string | null
  invitedBy: string
}

export async function listOrganizationUsers(): Promise<OrganizationUser[]> {
  return api.get<OrganizationUser[]>('/users')
}

export async function updateOrganizationUserRole(
  userId: string,
  role: OrgRole
): Promise<OrganizationUser> {
  return api.patch<OrganizationUser>(`/users/${userId}/role`, { role })
}

export async function updateOrganizationUserName(
  userId: string,
  fullName: string
): Promise<OrganizationUser> {
  return api.patch<OrganizationUser>(`/users/${userId}`, { fullName })
}

export async function removeOrganizationUser(userId: string): Promise<{ removed: boolean }> {
  return api.delete<{ removed: boolean }>(`/users/${userId}`)
}

export async function listPendingInvitations(): Promise<{ invitations: InvitationSummary[] }> {
  return api.get<{ invitations: InvitationSummary[] }>('/invitations')
}

export async function createInvitation(
  email: string,
  role: OrgRole
): Promise<{ invitation: InvitationSummary; inviteUrl?: string }> {
  return api.post<{ invitation: InvitationSummary; inviteUrl?: string }>('/invitations', { email, role })
}

export async function resendInvitation(
  invitationId: string
): Promise<{ invitation: InvitationSummary; inviteUrl?: string }> {
  return api.post<{ invitation: InvitationSummary; inviteUrl?: string }>(`/invitations/${invitationId}/resend`, {})
}

export async function revokeInvitation(invitationId: string): Promise<{ revoked: boolean }> {
  return api.delete<{ revoked: boolean }>(`/invitations/${invitationId}`)
}

// ===================
// Draft Segments (Agent Sessions v2)
// ===================

export type DraftSegmentStatus = 'fresh' | 'stale' | 'generating' | 'locked'

export interface CoverageRef {
  requirementId: string
  confidence: number
}

export interface CitationRef {
  citationIndex: number
  documentId: string
  documentName: string
  chunkIndex: number
  pageNumber: number | null
  /** V1 (PDF bbox) or V2 (text-search) highlight data for precise sentence highlighting. */
  highlightData?: SourceHighlightData | null
  /** Verified verbatim quote for the cited claim (quote-first highlighting). Absent on legacy segments. */
  quote?: string | null
  /** Source provenance — added in retrieval-safe-wins (PR 4). All optional. */
  folderPath?: string | null
  tags?: string[]
  qualityTier?: string
}

export interface DraftSegment {
  id: string
  sessionId: string
  sectionId: string
  heading: string
  content: string
  status: DraftSegmentStatus
  version: number
  wordCount: number
  editedWhileStale: boolean
  coverageRefs: CoverageRef[]
  citationRefs: CitationRef[]
  libraryCitationRefs: CitationRef[]
  createdAt: string
  updatedAt: string
}

export interface SaveSegmentRequest {
  content: string
  /** 0 = first save into a never-drafted section (creates the segment). */
  expectedVersion?: number
  version?: number
  wordCount: number
  /** Section title, used when the first save creates the segment row. */
  heading?: string
}

export interface SaveSegmentResponse {
  id: string
  sectionId: string
  heading: string
  version: number
  status: DraftSegmentStatus
  wordCount: number
}

// --- Draft Safety: version history, backup, presence ---

export type SegmentVersionSource =
  | 'manual_save'
  | 'ai_draft'
  | 'ai_revision'
  | 'conflict_backup'
  | 'manual_backup'
  | 'restore'

export interface SegmentVersionMeta {
  id: string
  source: SegmentVersionSource
  createdBy: { id: string; fullName: string | null } | null
  createdAt: string
  updatedAt: string
  wordCount: number
  version: number
}

export interface SegmentConflictDetails {
  currentVersion: number
  currentContent: string
  lastEditedBy: { id: string; fullName: string | null } | null
}

export interface PresenceEditor {
  userId: string
  fullName: string | null
}

// ===================
// Agent Sessions
// ===================

/**
 * The one AI revision a session still owes a review strip for. Returned by
 * `POST /agent/sessions`; the store rebuilds `draftPatchPreview` from it so
 * the Accept/Reject strip survives navigation (pending-proposal lifecycle).
 */
export interface PendingDraftProposal {
  proposalId: string
  turnId: string | null
  sectionId: string
  before: string
  after: string
  /** Null for a staged suggestion (R1): nothing has been applied yet. */
  appliedSegmentVersion: number | null
  /**
   * R1 W1. `applied` (and absent, on an older backend) = the AI text is already
   * in the section (R0 strip). `staged` = the section is untouched; the strip
   * shows the suggestion as a diff over the section's CURRENT text.
   */
  mode?: 'staged' | 'applied'
  segmentId?: string
  /** The section version a staged suggestion was made against (Accept compares it). */
  baseSegmentVersion?: number | null
  /** A staged suggestion's own [N] refs. Null = use the section's refs. */
  afterCitationRefs?: CitationRef[] | null
  /** A staged suggestion's own [LN] refs. Null = use the section's refs. */
  afterLibraryCitationRefs?: CitationRef[] | null
  /** Section title, when the backend sends one (per-document suggestion list). */
  heading?: string | null
}

export interface AgentSessionResponse {
  sessionId: string
  status: string
  artifactId: string
  sectionKey: string
  activePlanId: string | null
  /**
   * The SESSION's mirror of the plan version. Can LAG the plan row when an
   * inline edit loses the session's optimistic lock (the backend warns and
   * proceeds by design). Don't use it as `expectedVersion`.
   */
  activePlanVersion?: number
  /**
   * The plan ROW's version — the number `PATCH /plans/{id}` compares
   * `expectedVersion` against. Absent on older responses.
   */
  activePlanRowVersion?: number | null
  activeDraftVersion?: number
  activePlan?: Record<string, unknown> | null
  /**
   * Status of the active plan (plan-card-ux §5b A2). Drives pending-plan-card
   * rehydration: the card only reappears when this is 'draft'.
   */
  activePlanStatus?: string | null
  planSectionOrder?: string[]
  segments?: DraftSegment[]
  activeThreadId?: string | null
  threads?: AgentThread[]
  turnHistory?: AgentChatTurnHistoryEntry[]
  /**
   * Approved plans that turns in `turnHistory` produced
   * (`assistantMetadata.producedPlan`). The store puts each back as a collapsed
   * "Plan approved" card after the turn that produced it. Absent on older
   * responses.
   */
  approvedPlans?: Array<{
    planId: string
    version: number
    plan: Record<string, unknown>
  }>
  isLocked?: boolean
  pendingProposal?: PendingDraftProposal | null
  /**
   * R1 W2: every staged suggestion still waiting on this document, one per
   * section. Not sent by the backend yet (W1 returns only `pendingProposal`);
   * when absent the per-document list falls back to `pendingProposal` and to
   * suggestions that arrive live.
   */
  pendingSuggestions?: PendingDraftProposal[]
  /**
   * R1 E1f: the whole-document job is offered in this panel (the server's
   * switch is on and the document is narrative), so a request about several
   * sections goes to the turn, where start_job takes it. Absent (older
   * responses) reads as off.
   */
  documentJobEnabled?: boolean
  createdAt: string
}

/**
 * A single persisted turn from a chat/agent session's active thread. This is the
 * ONE shared shape returned by `POST /agent/sessions` for every scope kind
 * (unit/artifact editor sessions and bid/org chat-lane sessions), so it carries
 * the union of everything a consumer might read: the editor's `suggestedActions`
 * offer AND the chat lane's persisted citations / honest-status fields. Every
 * field beyond the turn identity is optional — each surface reads only its own.
 * Typing it here removes the tender store's `turnHistory` double cast (both chat
 * stores consume this type directly).
 */
export interface AgentChatTurnHistoryEntry {
  turnId: string
  threadId?: string | null
  userMessage: string
  assistantResponse: string | null
  assistantMetadata?: {
    suggestedActions?: {
      offerId: string
      sourceTurnId: string
      recommendedActionId: string
      expiresAt: string
      actions: Array<{
        actionId: string
        label: string
        utterance: string
      }>
    }
    citations?: Array<{
      documentId: string
      documentName: string
      pageNumber: number | null
      snippet?: string
      quote?: string
      highlightData?: unknown
    }>
    libraryCitations?: Array<{
      documentId: string
      documentName: string
      pageNumber: number | null
      snippet?: string
      quote?: string
      highlightData?: unknown
    }>
    /** `weak` = results returned, but none scored like a real match. */
    retrievalStatus?: 'ok' | 'partial' | 'empty' | 'weak'
    fallbackUsed?: boolean
    /**
     * Quick-write document produced by this turn (Assistant door). Persisted
     * whole so the chip can re-open it from history without another fetch.
     */
    artifact?: {
      title: string
      content: string
      truncated?: boolean
    }
    /**
     * The plan this turn produced (its plan card). Links an approved plan to
     * its place in the conversation on reload. Absent on older turns.
     */
    producedPlan?: {
      planId: string
      version: number
    }
  } | null
  /**
   * Display refs for the files the user attached to this turn. Persisted
   * without the s3Key — the chips are rendered from this alone.
   */
  attachments?: Array<{
    attachmentId: string
    filename: string
    mimeType: string
    size: number
  }>
  createdAt: string
  completedAt: string | null
}

export type AgentThreadCarryMode = 'carry_summary' | 'fresh'

export interface ReviewFindingThreadRef {
  runId: string
  findingIndex: number
  criterionName: string
  severity: 'high' | 'medium' | 'low'
}

export interface CommentFixThreadRef {
  commentId: string
  sectionId: string | null
  quotedText: string | null
}

export interface AgentThread {
  id: string
  sessionId: string
  name: string
  carryMode: AgentThreadCarryMode
  isPinned: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
  reviewFindingRef?: ReviewFindingThreadRef | null
  commentFixRef?: CommentFixThreadRef | null
}

export interface AgentThreadsResponse {
  sessionId: string
  activeThreadId: string | null
  threads: AgentThread[]
}

export interface CreateAgentThreadResponse {
  thread: AgentThread
  activeThreadId: string
  carriedSummary: boolean
}

/**
 * Get or create an active agent session for an artifact section.
 * Safe to call on every panel mount — returns existing session if one exists.
 */
export async function getOrCreateAgentSession(
  bidId: string,
  artifactId: string,
  sectionKey: string,
  scopeKind: 'unit' | 'artifact' = 'unit'
): Promise<AgentSessionResponse> {
  return api.post<AgentSessionResponse>('/agent/sessions', { bidId, artifactId, sectionKey, scopeKind })
}

/**
 * A single turn from a chat-lane session's active thread, shaped for hydration
 * into a chat message list. Citations live under `assistantMetadata` (see
 * backend AssistantTurnMetadata). `retrievalStatus`/`fallbackUsed` are persisted
 * too, so the honest-status line survives a reload. Alias of the shared
 * `AgentChatTurnHistoryEntry` (both bid and org chat sessions use it).
 */
export type BidChatTurnHistoryEntry = AgentChatTurnHistoryEntry

/**
 * The normalized shape both chat-lane stores (tender + assistant) consume from
 * `POST /agent/sessions`: the active thread, the thread list, and the active
 * thread's persisted history. Scope-agnostic — bid and org sessions return it.
 */
export interface ChatLaneSessionResponse {
  sessionId: string
  activeThreadId: string | null
  threads: AgentThread[]
  turnHistory: AgentChatTurnHistoryEntry[]
}

/** @deprecated bid-named alias — use `ChatLaneSessionResponse`. */
export type BidChatSessionResponse = ChatLaneSessionResponse

/**
 * Normalize a raw `POST /agent/sessions` response into the chat-lane shape.
 * Shared by the bid and org wrappers — the backend returns the same shape for
 * both scope kinds (see agent-api `handleGetOrCreateSession`).
 */
function toChatLaneSessionResponse(raw: AgentSessionResponse): ChatLaneSessionResponse {
  return {
    sessionId: raw.sessionId,
    activeThreadId: raw.activeThreadId ?? null,
    threads: raw.threads ?? [],
    turnHistory: raw.turnHistory ?? [],
  }
}

/**
 * Get or create the per-user bid-scoped chat session (tender chat lane).
 * Backend enforces one session per (user, bid); safe to call on every mount.
 * Returns the active thread, the thread list, and the active thread's history.
 */
export async function getOrCreateBidChatSession(
  bidId: string
): Promise<ChatLaneSessionResponse> {
  const raw = await api.post<AgentSessionResponse>('/agent/sessions', {
    scopeKind: 'bid',
    bidId,
  })
  return toChatLaneSessionResponse(raw)
}

/**
 * Get or create the per-user org-scoped chat session (Assistant door — library
 * lane). Backend enforces one active org chat session per user; safe to call on
 * every panel open / page mount. Same response shape as the bid session.
 */
export async function getOrCreateOrgChatSession(): Promise<ChatLaneSessionResponse> {
  const raw = await api.post<AgentSessionResponse>('/agent/sessions', {
    scopeKind: 'org',
  })
  return toChatLaneSessionResponse(raw)
}

export async function listAgentThreads(sessionId: string): Promise<AgentThreadsResponse> {
  return api.get<AgentThreadsResponse>(`/agent/sessions/${sessionId}/threads`)
}

export async function createAgentThread(
  sessionId: string,
  carryMode: AgentThreadCarryMode = 'carry_summary',
  name?: string
): Promise<CreateAgentThreadResponse> {
  return api.post<CreateAgentThreadResponse>(
    `/agent/sessions/${sessionId}/threads`,
    {
      carryMode,
      ...(name ? { name } : {}),
    }
  )
}

export async function activateAgentThread(
  sessionId: string,
  threadId: string
): Promise<{ sessionId: string; activeThreadId: string }> {
  return api.post<{ sessionId: string; activeThreadId: string }>(
    `/agent/sessions/${sessionId}/threads/${threadId}/activate`
  )
}

/**
 * Rename and/or pin-toggle a thread. Both fields are optional — send only what
 * changed. Returns the updated thread.
 */
export async function updateAgentThread(
  sessionId: string,
  threadId: string,
  updates: { name?: string; isPinned?: boolean }
): Promise<{ thread: AgentThread }> {
  return api.patch<{ thread: AgentThread }>(
    `/agent/sessions/${sessionId}/threads/${threadId}`,
    updates
  )
}

/**
 * Delete a thread. Returns the remaining threads and the session's active
 * thread id — the backend reassigns the active thread (or seeds a fresh one
 * when the last thread was deleted), so callers should adopt both values
 * rather than computing the next active thread themselves.
 */
export async function deleteAgentThread(
  sessionId: string,
  threadId: string
): Promise<{ threads: AgentThread[]; activeThreadId: string | null }> {
  return api.delete<{ threads: AgentThread[]; activeThreadId: string | null }>(
    `/agent/sessions/${sessionId}/threads/${threadId}`
  )
}

/**
 * Render a quick-write document to .docx.
 *
 * The server renders from the copy persisted on the turn — the content is never
 * sent up — so this only needs the ids. The body comes back base64-encoded
 * because the REST API is JSON-only; the caller turns it into a Blob download.
 */
export async function exportQuickDraftDocx(
  sessionId: string,
  turnId: string
): Promise<{ filename: string; contentBase64: string }> {
  // turnId rides the body, not the path — the REST API sits at the hard
  // 300-resource API Gateway cap and the flat path shape costs one resource.
  return api.post<{ filename: string; contentBase64: string }>(
    `/agent/sessions/${sessionId}/export-docx`,
    { turnId }
  )
}

export interface ActiveTurnInfo {
  turnId: string
  sessionId: string
  status: 'started' | 'streaming' | 'async_pending'
  intentSelected: 'chat' | 'plan' | 'write'
  userMessage: string
  asyncJobId: string | null
  threadId: string | null
  createdAt: string
}

export interface ActiveTurnResponse {
  turn: ActiveTurnInfo | null
}

/**
 * Fetch the in-flight (worker-blocking) turn for a session, if any.
 * Backend runs a check-on-read reaper — stale turns are flipped to failed
 * and this returns { turn: null }. Used on section re-entry to resume UI
 * state when a previous turn is still processing, and to detect ghost
 * turns before submitting a new one.
 */
export async function getActiveTurn(sessionId: string): Promise<ActiveTurnResponse> {
  return api.get<ActiveTurnResponse>(`/agent/sessions/${sessionId}/active-turn`)
}

export interface CancelAgentTurnRequest {
  turnId?: string
  sessionId?: string
}

export interface CancelAgentTurnResponse {
  cancelled: boolean
  turnId?: string
  jobId?: string
  type?: 'sync' | 'async'
  message?: string
}

/**
 * Cancel an active agent turn or async job. Flips the DB row to 'cancelled'
 * on the backend — safe to call even after the user has navigated away from
 * the originating section.
 */
export async function cancelAgentTurn(
  request: CancelAgentTurnRequest
): Promise<CancelAgentTurnResponse> {
  return api.post<CancelAgentTurnResponse>('/agent/cancel', request)
}

/**
 * How long a segment save may take before the client gives up on it.
 *
 * It must never cut off a save that is still alive — cold starts alone take
 * several seconds — and a live one cannot outlast the server: API Gateway
 * ends the request at ~29 s (504) and the agent-api Lambda is stopped at 30 s
 * (`Timeout: 30`, backend/template.yaml). Past 35 s the request is dead and
 * the server has finished; the 5 s over the Lambda's limit covers reading the
 * auth session and uploading the body, which happen before the server's clock
 * starts. Do not go below 30 s.
 *
 * Without it, a request that never answered (a stalled connection) held the
 * editor's one-in-flight save queue, so every later autosave waited behind it.
 */
export const SEGMENT_SAVE_TIMEOUT_MS = 35_000

/**
 * Manually save segment content from the editor — Spec 08 §6.6.
 * Direct REST call (not an agent turn). Optimistic lock via version.
 * Throws ApiRequestError with statusCode 409 on version conflict, and with
 * code 'TIMEOUT' when no answer comes within SEGMENT_SAVE_TIMEOUT_MS. A timed-
 * out save may still have been applied by the server; the editor's retry
 * reconciles that (see `components/drafting/unconfirmedSaves.ts`).
 *
 * Diff decorations are stripped here rather than at each call site: this is the
 * one chokepoint every draft write passes through (autosave, conflict recovery,
 * partial-hunk accept), and an un-accepted suggestion's `<ins>`/`<del>` markup
 * must never become stored draft content (E2E F1). See `stripDiffMarkup`.
 */
export async function saveSegment(
  sessionId: string,
  sectionId: string,
  request: SaveSegmentRequest
): Promise<SaveSegmentResponse> {
  return apiRequest<SaveSegmentResponse>(
    `/agent/sessions/${sessionId}/segments/${sectionId}`,
    {
      method: 'PATCH',
      body: { ...request, content: stripDiffMarkup(request.content) },
      timeoutMs: SEGMENT_SAVE_TIMEOUT_MS,
    }
  )
}

const segmentPath = (sessionId: string, sectionId: string) =>
  `/agent/sessions/${sessionId}/segments/${sectionId}`

/** List all saved versions of a segment (Draft Safety §version-history). */
export async function listSegmentVersions(
  sessionId: string,
  sectionId: string
): Promise<{ versions: SegmentVersionMeta[] }> {
  return api.patch<{ versions: SegmentVersionMeta[] }>(
    segmentPath(sessionId, sectionId),
    { action: 'list_versions' }
  )
}

/** Fetch a single version including its full content. */
export async function getSegmentVersion(
  sessionId: string,
  sectionId: string,
  versionId: string
): Promise<SegmentVersionMeta & { content: string }> {
  return api.patch<SegmentVersionMeta & { content: string }>(
    segmentPath(sessionId, sectionId),
    { action: 'get_version', versionId }
  )
}

/** Restore a prior version as the segment's current content. */
export async function restoreSegmentVersion(
  sessionId: string,
  sectionId: string,
  versionId: string
): Promise<SaveSegmentResponse> {
  return api.patch<SaveSegmentResponse>(
    segmentPath(sessionId, sectionId),
    { action: 'restore', versionId }
  )
}

/**
 * Back up in-editor content that would otherwise be lost.
 * `reason: 'conflict'` (default) — auto-backup during Load-Theirs conflict resolution.
 * `reason: 'manual'` — user-initiated backup from the blocked editor state; the server
 * records it as `manual_backup` and skips the writable guard (safe: append-only).
 */
export async function backupSegmentContent(
  sessionId: string,
  sectionId: string,
  content: string,
  baseVersion: number,
  reason: 'conflict' | 'manual' = 'conflict'
): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(
    segmentPath(sessionId, sectionId),
    { action: 'backup', content: stripDiffMarkup(content), baseVersion, reason }
  )
}

/** Announce that the current user is editing this segment; returns other active editors. */
export async function sendEditingPresence(
  sessionId: string,
  sectionId: string
): Promise<{ editors: PresenceEditor[] }> {
  return api.patch<{ editors: PresenceEditor[] }>(
    segmentPath(sessionId, sectionId),
    { action: 'presence' }
  )
}

// ===================
// Prompt Templates API
// ===================

export interface PromptCategory {
  id: string
  organizationId: string
  name: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface PromptTemplate {
  id: string
  organizationId: string
  title: string
  content: string
  categoryId: string | null
  categoryName: string | null
  visibility: 'personal' | 'shared'
  isPinned: boolean
  createdBy: string
  creatorName: string
  createdAt: string
  updatedAt: string
}

// --- Categories ---

export async function listPromptCategories(): Promise<PromptCategory[]> {
  const data = await api.get<{ categories: PromptCategory[] }>('/prompt-templates/categories')
  return data.categories
}

export async function createPromptCategory(data: { name: string }): Promise<PromptCategory> {
  return api.post<PromptCategory>('/prompt-templates/categories', data)
}

export async function updatePromptCategory(
  id: string,
  data: { name: string },
): Promise<PromptCategory> {
  return api.patch<PromptCategory>(`/prompt-templates/categories/${id}`, data)
}

export async function deletePromptCategory(id: string): Promise<void> {
  await api.delete(`/prompt-templates/categories/${id}`)
}

// --- Prompt Templates ---

export async function listPromptTemplates(): Promise<PromptTemplate[]> {
  const data = await api.get<{ prompts: PromptTemplate[] }>('/prompt-templates')
  return data.prompts
}

export async function createPromptTemplate(data: {
  title: string
  content: string
  categoryId?: string | null
  visibility?: 'personal' | 'shared'
  isPinned?: boolean
}): Promise<PromptTemplate> {
  return api.post<PromptTemplate>('/prompt-templates', data)
}

export async function updatePromptTemplate(
  id: string,
  data: {
    title?: string
    content?: string
    categoryId?: string | null
    visibility?: 'personal' | 'shared'
    isPinned?: boolean
  },
): Promise<PromptTemplate> {
  return api.patch<PromptTemplate>(`/prompt-templates/${id}`, data)
}

export async function deletePromptTemplate(id: string): Promise<void> {
  await api.delete(`/prompt-templates/${id}`)
}

// ===================
// Organization Lifecycle (GDPR)
// ===================

import type {
  OrgDeletionRequest,
  DeletionStatus,
  OrgExportJob,
  ExportSizeEstimate,
  ActiveSessions,
  SupportAccessGrant,
  GrantSupportAccessInput,
} from '@/lib/schemas'

/** Schedule a 7-day deletion of the current org. Admin-only. */
export async function scheduleOrgDeletion(): Promise<OrgDeletionRequest> {
  return apiRequest<OrgDeletionRequest>('/organizations/me', { method: 'DELETE' })
}

/** Cancel a pending deletion during the grace period. Admin-only. */
export async function cancelOrgDeletion(): Promise<OrgDeletionRequest> {
  return apiRequest<OrgDeletionRequest>('/organizations/me/cancel-deletion', { method: 'POST' })
}

/** Get the current deletion status (none or pending). */
export async function getDeletionStatus(): Promise<DeletionStatus> {
  return apiRequest<DeletionStatus>('/organizations/me/deletion-status')
}

/** Count active in-flight jobs that would be cancelled by a deletion. */
export async function getActiveSessions(): Promise<ActiveSessions> {
  return apiRequest<ActiveSessions>('/organizations/me/active-sessions')
}

/** Pre-flight size estimate for an export (tab load). */
export async function getExportSizeEstimate(): Promise<ExportSizeEstimate> {
  return apiRequest<ExportSizeEstimate>('/organizations/me/export/estimate')
}

/** Start a new export job. Admin-only. Rate-limited to 1 per 24h. */
export async function startOrgExport(): Promise<{
  jobId: string
  estimatedSizeBytes: number
  estimatedFileCount: number
  estimatedDurationSeconds: number
}> {
  return apiRequest('/organizations/me/export', { method: 'POST' })
}

/** List past export jobs for the current org. */
export async function listOrgExports(): Promise<{ items: OrgExportJob[] }> {
  return apiRequest<{ items: OrgExportJob[] }>('/organizations/me/exports')
}

/** Get a single export job's status (for polling). */
export async function getOrgExport(jobId: string): Promise<OrgExportJob> {
  return apiRequest<OrgExportJob>(`/organizations/me/exports/${jobId}`)
}

// --- Support access (org admins only) ---

/** List support-access grants, newest first (active, expired and revoked). */
export async function listSupportAccessGrants(): Promise<SupportAccessGrant[]> {
  const data = await apiRequest<{ grants: SupportAccessGrant[] }>('/organizations/me/support-access')
  return data.grants
}

/** Let the BidContext team see the AI's working for 1–14 days. 400 carries field errors. */
export async function grantSupportAccess(input: GrantSupportAccessInput): Promise<SupportAccessGrant> {
  const data = await apiRequest<{ grant: SupportAccessGrant }>('/organizations/me/support-access', {
    method: 'POST',
    body: input,
  })
  return data.grant
}

/** End a grant now. Safe to repeat. */
export async function revokeSupportAccess(grantId: string): Promise<SupportAccessGrant> {
  const data = await apiRequest<{ grant: SupportAccessGrant }>('/organizations/me/support-access', {
    method: 'PATCH',
    body: { grantId, action: 'revoke' },
  })
  return data.grant
}

// ===================
// SharePoint Integration
// ===================

export interface SharePointConnectionSummary {
  id: string
  userPrincipalName: string
  displayName: string | null
  connectedAt: string
  lastUsedAt: string | null
  status: 'active' | 'expired' | 'revoked'
}

export interface SharePointStatusResponse {
  connected: boolean
  connection: SharePointConnectionSummary | null
}

export interface SharePointSite {
  id: string
  displayName: string
  webUrl: string
  description?: string
}

export interface SharePointDrive {
  id: string
  name: string
  driveType: string
  webUrl: string
}

export interface SharePointItem {
  id: string
  name: string
  size: number
  webUrl: string
  lastModifiedDateTime: string
  lastModifiedBy: string | null
  isFolder: boolean
  childCount?: number
  mimeType: string | null
  etag: string | null
  parentPath: string | null
  alreadyImported: boolean
}

export interface SharePointItemsResponse {
  items: SharePointItem[]
  nextPage: string | null
}

export type SharePointImportDestination =
  | { type: 'library'; folderId: string }
  | { type: 'bid'; bidId: string }

export interface SharePointImportRequest {
  destination: SharePointImportDestination
  items: Array<{ siteId: string; driveId: string; itemId: string }>
}

export interface SharePointImportResponse {
  jobId: string | null
  itemsTotal: number
  skippedCount: number
  message?: string
}

export interface SharePointExportRequest {
  artifactId: string
  templateId?: string | null
  sectionKey?: string | null
  destination: {
    siteId: string
    driveId: string
    folderId: string
    folderPath?: string | null
  }
  filename: string
  conflictBehavior: 'rename' | 'replace' | 'fail'
  /** The user confirmed the template's own pages may be replaced (D4). */
  acknowledgeTemplateLoss?: boolean
}

export interface SharePointJobStatusResponse {
  kind: 'import' | 'export'
  status: string
  // Import-only
  itemsTotal?: number
  itemsDone?: number
  itemsFailed?: number
  itemsSkipped?: number
  // Export-only
  filename?: string
  itemWebUrl?: string | null
  sizeBytes?: number | null
  // Both
  errorMessage: string | null
  completedAt: string | null
}

/**
 * Begin the OAuth flow. Returns a Microsoft authorize URL; the caller then
 * navigates the user to it. `returnTo` controls where the user lands after
 * the callback completes (whitelisted server-side).
 */
export async function getSharePointAuthUrl(returnTo?: string): Promise<{ authUrl: string }> {
  return apiRequest<{ authUrl: string }>('/sharepoint/auth-url', {
    params: returnTo ? { returnTo } : undefined,
  })
}

export async function getSharePointStatus(): Promise<SharePointStatusResponse> {
  return apiRequest<SharePointStatusResponse>('/sharepoint/status')
}

export async function disconnectSharePoint(): Promise<void> {
  await apiRequest('/sharepoint/connection', { method: 'DELETE' })
}

export async function listSharePointSites(searchQuery?: string): Promise<{ sites: SharePointSite[] }> {
  return apiRequest<{ sites: SharePointSite[] }>('/sharepoint/sites', {
    params: searchQuery ? { search: searchQuery } : undefined,
  })
}

/**
 * Look up a SharePoint site by URL — bypasses Microsoft's search indexing
 * (24–48h delay on new tenants, and Team-backed sites are sometimes filtered).
 */
export async function getSharePointSiteByUrl(url: string): Promise<{ site: SharePointSite }> {
  return apiRequest<{ site: SharePointSite }>('/sharepoint/site-by-url', { params: { url } })
}

export async function listSharePointDrives(siteId: string): Promise<{ drives: SharePointDrive[] }> {
  return apiRequest<{ drives: SharePointDrive[] }>('/sharepoint/drives', {
    params: { siteId },
  })
}

export async function listSharePointItems(
  driveId: string,
  folderId?: string,
  nextPage?: string,
  profile?: 'chat',
): Promise<SharePointItemsResponse> {
  const params: Record<string, string> = { driveId }
  if (folderId) params['folderId'] = folderId
  if (nextPage) params['nextPage'] = nextPage
  if (profile) params['profile'] = profile
  return apiRequest<SharePointItemsResponse>('/sharepoint/items', { params })
}

export async function importSharePointFiles(
  body: SharePointImportRequest,
  idempotencyKey?: string,
): Promise<SharePointImportResponse> {
  // exactOptionalPropertyTypes: only attach `headers` when we have a value.
  if (idempotencyKey) {
    return apiRequest<SharePointImportResponse>('/sharepoint/import', {
      method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey },
    })
  }
  return apiRequest<SharePointImportResponse>('/sharepoint/import', { method: 'POST', body })
}

export async function exportToSharePoint(
  body: SharePointExportRequest,
  idempotencyKey?: string,
): Promise<{ jobId: string }> {
  if (idempotencyKey) {
    return apiRequest<{ jobId: string }>('/sharepoint/export', {
      method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey },
    })
  }
  return apiRequest<{ jobId: string }>('/sharepoint/export', { method: 'POST', body })
}

export async function getSharePointJobStatus(jobId: string): Promise<SharePointJobStatusResponse> {
  return apiRequest<SharePointJobStatusResponse>(`/sharepoint/jobs/${jobId}`)
}

// ===================
// Submission Pack (Specs/submission-pack-spec.md)
//
// One read — `GET /bids/{id}/submission-pack` — assembles the checklist, its
// resolved links, the derived readiness verdict and the bid-level roll-up.
// The types below mirror `backend/src/lib/submission-pack.ts` field-for-field;
// change one, change both.
//
// Writes have NO pack-specific route: they ride the existing extraction
// endpoints the Analysis lens already uses, then the store re-reads the pack.
// ===================

export type SubmissionStatus = 'not_ready' | 'in_progress' | 'ready' | 'not_required'
export type StatusSource = 'derived' | 'manual'
export type DerivedReadiness = 'not_ready' | 'in_progress' | 'ready'

export interface ReadinessResult {
  readiness: DerivedReadiness
  /** Parsed words; null when the limit is pages-only or unparsed (display-only). */
  wordLimit: number | null
  /** Summed linked-section word counts (response class only). */
  totalWords: number | null
  /** False whenever wordLimit is null — display-only limits never fail an item. */
  overWordLimit: boolean
}

export type PackItemLink =
  | {
      kind: 'artifact'
      artifactId: string
      artifactName: string
      sectionKey: string | null
      sectionTitle: string | null
      artifactType: string
    }
  | {
      kind: 'document'
      documentId: string
      documentName: string
      sizeBytes: number | null
      mimeType: string | null
    }

export interface PackItem {
  id: string
  title: string
  description: string | null
  triageClass: TriageClass | null
  format: string | null
  limit: string | null
  /** The server stringifies an INTEGER column — not a number. */
  copies: string | null
  method: string | null
  sourceQuote: string | null
  sourcePageNumber: number | null
  sourceDocumentId: string | null
  ownerId: string | null
  note: string | null
  /** 'ai' | 'manual' — where the row came from. */
  extractionSource: string
  lifecycleState: 'active' | 'superseded'
  /** The STORED status (only meaningful when statusSource is 'manual'). */
  status: SubmissionStatus
  statusSource: StatusSource
  /** EFFECTIVE state: manual status wins, otherwise the derived verdict. */
  readiness: SubmissionStatus
  /** wordLimit / totalWords / overWordLimit for display. */
  derived: ReadinessResult
  link: PackItemLink | null
  acknowledgedAt: string | null
}

export interface SubmissionPackRollup {
  ready: number
  inProgress: number
  notReady: number
  notRequired: number
  /** Excludes not_required — it is not work. */
  total: number
}

export interface SubmissionPackBlockers {
  mandatoryAtRisk: number
  /** not_ready attachment/form items. */
  missingHandovers: number
  /** not_ready response items. */
  undraftedResponses: number
  /** not_ready acknowledge items. */
  unacknowledged: number
  overWordLimit: number
  openTodos: number
  removedByAddendum: number
}

// --- "Needs from you" (spec P3, decision D-15) ---

export type PackAskKind = 'manual' | 'todo' | 'fact' | 'question'

/** Where an ask came from: the job that raised it, and the fact or section it concerns. */
export type PackAskSourceRef =
  | { jobId: string; factKey: string }
  | { jobId: string; sectionId: string; checklistRef?: string }

/**
 * A persisted ask. Three shapes in one table:
 *  - `manual` — an ask a human typed.
 *  - `todo`   — an OVERLAY on a `[TODO: …]` marker inside a draft. The marker is
 *    inline text, not a row, so the overlay is keyed by artifact + section +
 *    text and carries the answer / owner / resolve state the marker cannot hold.
 *  - `fact`   — a fact the drafting job wants a person to confirm. Only the job
 *    creates these; the create route refuses them from a user.
 */
export interface PackAsk {
  id: string
  kind: PackAskKind
  text: string
  artifactId: string | null
  sectionId: string | null
  ownerId: string | null
  answer: string | null
  resolvedAt: string | null
  createdAt: string
  /** Absent from a backend older than migration 193. */
  sourceRef?: PackAskSourceRef | null
  /** Present on `question` rows only (R1 Q1, migration 197). */
  question?: QuestionDetails
}

// --- Durable questions (R1 Q1/Q2, technical design §10.4) ---

export type QuestionKind = 'fact' | 'judgement' | 'decision'
export type QuestionStatus = 'open' | 'answered' | 'deferred' | 'incorporated' | 'superseded'
export type QuestionAnswerEffect = 'answer_only' | 'update_draft'

/** An answer option. The option carrying `reason` is the recommendation (never on a fact question). */
export interface QuestionOption {
  label: string
  reason?: string
}

/** A section the answer affects, with the version it had when the question was raised. */
export interface QuestionTarget {
  artifactId: string
  sectionKey: string
  version: number | null
}

export interface QuestionDetails {
  questionKind: QuestionKind | null
  /** Why the AI asks. */
  reason: string | null
  options: QuestionOption[]
  targets: QuestionTarget[]
  jobId: string | null
  sessionId: string | null
  status: QuestionStatus
  answerEffect: QuestionAnswerEffect | null
  /** The continuation job started for the current answer. */
  incorporatedByJobId: string | null
  /** Who answered it (or replaced it). */
  resolvedBy: string | null
}

/** A `question` ask: always carries its details. */
export type QuestionAsk = PackAsk & { kind: 'question'; question: QuestionDetails }

export interface QuestionSkippedTarget {
  sectionKey: string
  reason: string
}

/** What "Save and update the draft" did about the draft. */
export type QuestionContinuation =
  | { state: 'started'; jobId: string; targets: string[]; skipped: QuestionSkippedTarget[] }
  | { state: 'already_started'; jobId: string }
  /** Another drafting job holds the document: the update runs when it finishes. */
  | { state: 'busy'; jobId: string; skipped: QuestionSkippedTarget[]; message: string }
  | { state: 'no_targets'; skipped: QuestionSkippedTarget[] }
  /** The whole-document job is switched off: the answer was saved only, nothing queued (R1 J8′). */
  | { state: 'unavailable'; message: string }

/**
 * What a panel answer turn recorded (`question_answered` event, R1 Q2), in the
 * shape the question PATCH returns. `continuation: null` = answer only.
 */
export type QuestionAnswerOutcome =
  | { kind: 'answered'; effect: QuestionAnswerEffect; repeated: boolean; continuation: QuestionContinuation | null }
  | { kind: 'deferred'; repeated: boolean }
  | { kind: 'not_recorded'; code: 'question_superseded' | 'question_already_answered' }

export interface QuestionActionResponse {
  ask: PackAsk
  repeated: boolean
  /** Present on an answer; null for "Save answer". */
  continuation?: QuestionContinuation | null
}

/**
 * Every question raised on one document (replaced ones excluded), open first.
 * `canUpdateDraft`: whether a card may offer "Save and update the draft" —
 * computed by the server from the whole-document job's switch (R1 J8′);
 * absent (an older server) means no.
 */
export async function listDocumentQuestions(
  bidId: string,
  artifactId: string
): Promise<{ asks: PackAsk[]; canUpdateDraft?: boolean }> {
  return api.get<{ asks: PackAsk[]; canUpdateDraft?: boolean }>(`/bids/${bidId}/pack-asks`, { kind: 'question', artifactId })
}

export async function answerQuestion(
  bidId: string,
  askId: string,
  answer: string,
  effect: QuestionAnswerEffect
): Promise<QuestionActionResponse> {
  return api.patch<QuestionActionResponse>(`/bids/${bidId}/pack-asks/${askId}`, {
    action: 'answer',
    answer,
    effect,
  })
}

/** "Not known yet". */
export async function deferQuestion(bidId: string, askId: string): Promise<QuestionActionResponse> {
  return api.patch<QuestionActionResponse>(`/bids/${bidId}/pack-asks/${askId}`, { action: 'defer' })
}

/** One `[TODO: …]` marker found in a draft. `sectionId` IS the outline section key. */
export interface BidTodo {
  artifactId: string
  artifactName: string
  /** Editor family for the deep-link — the scan covers every artifact type, not just prose. */
  artifactType: ArtifactType
  scopeKind: 'unit' | 'artifact'
  /**
   * The raw draft-segment id. It is the overlay identity `pack_asks` rows are
   * keyed on, NOT a route key — for prose drafts it is commonly a sub-heading
   * slug no outline entry names. Route with `outlineSectionKey`.
   */
  sectionId: string
  /**
   * The outline `sectionKey` this marker sits under, resolved server-side.
   * null when nothing matched — the link then falls back to artifact level
   * rather than a section route that renders "Section not found".
   */
  outlineSectionKey: string | null
  /** null when the segment matched no outline entry — the UI then shows the artifact alone. */
  sectionTitle: string | null
  text: string
}

/**
 * One row of "Needs from you". Three sources, one list. Todos are deduped by
 * artifact + section + text; `occurrences` (≥ 1) counts the identical markers.
 */
export type NeedsFromYouEntry =
  | { kind: 'todo'; todo: BidTodo; overlay: PackAsk | null; occurrences: number }
  | { kind: 'fact'; ask: PackAsk }
  | { kind: 'question'; ask: PackAsk; location?: PackQuestionLocation }
  | { kind: 'manual'; ask: PackAsk }

/**
 * Where an AI question's answer goes (R1 J-tune18, hand test F06): the
 * document and, for an outline document, the section, resolved server-side so
 * the row links to it as a todo row does. Absent on an older server, or when
 * the document is gone.
 */
export interface PackQuestionLocation {
  artifactId: string
  artifactName: string
  artifactType: ArtifactType
  scopeKind: 'unit' | 'artifact'
  /** The outline section; null on a single document, or when the section left the outline. */
  sectionKey: string | null
  sectionTitle: string | null
}

export interface SubmissionPackResponse {
  items: PackItem[]
  removedByAddendum: PackItem[]
  rollup: SubmissionPackRollup
  blockers: SubmissionPackBlockers
  /** Distinct open todos (less resolved overlays) + unresolved fact and manual asks. */
  needsFromYou: NeedsFromYouEntry[]
  /** Always `needsFromYou.length` — deduped, so it can be below `blockers.openTodos`. */
  needsFromYouCount: number
  deadline: string | null
  /** Whether `deadline` is an exact closing time; null = not recorded (older bids). */
  deadlineHasTime?: boolean | null
}

/**
 * PATCH body for one checklist item.
 *
 * `linkedArtifactId` and `linkedDocumentId` must never both be non-null in one
 * body — the server 400s (a row links to an artifact OR a document).
 */
export interface PackItemPatch {
  status?: SubmissionStatus
  /** 'derived' resets a manual status back to the computed verdict. */
  statusSource?: StatusSource
  ownerId?: string | null
  linkedArtifactId?: string | null
  linkedSectionKey?: string | null
  linkedDocumentId?: string | null
  /** A flag, not a timestamp — the server stamps NOW() itself. */
  acknowledge?: boolean
  triageClass?: TriageClass
  note?: string | null
}

/** Body for a manual pack row. `triageClass` defaults to 'attachment' server-side. */
export interface CreatePackItemInput {
  title: string
  triageClass?: TriageClass
  description?: string | null
  format?: string | null
  limit?: string | null
  copies?: number | null
  method?: string | null
  ownerId?: string | null
  note?: string | null
}

export async function getSubmissionPack(bidId: string): Promise<SubmissionPackResponse> {
  return api.get<SubmissionPackResponse>(`/bids/${bidId}/submission-pack`)
}

/**
 * A pack document that went out with last accepted text because an AI change
 * in it still awaits Accept/Reject (R0-A). `leftOut` sections had none.
 */
export interface PackPendingAiChange {
  itemTitle: string
  sections: string[]
  leftOut?: string[]
}

type PackExportStatusResponse =
  | { status: 'running' }
  | {
      status: 'complete'
      downloadUrl: string
      filename: string
      sizeBytes: number
      missingCount: number
      /** Absent from a backend older than R0-A. */
      pendingAiChanges?: PackPendingAiChange[]
    }
  | { status: 'failed'; code: string; message: string }

/**
 * Ten minutes, not the three the single-artifact export uses: a pack is a zip
 * of every linked response, workbook and attachment, so it legitimately runs an
 * order of magnitude longer than one .docx render.
 */
const PACK_EXPORT_POLL_TIMEOUT_MS = 10 * 60_000

/**
 * Export the whole submission pack as a zip (spec P4).
 *
 * Start + poll, the `exportArtifactToDocx` shape: the worker runs outside the
 * API Gateway 29s cap, this resolves once the browser download has fired.
 *
 * `templateId` is OMITTED from the body when absent — the route rejects a
 * literal `null` (it validates the key as a UUID whenever it is present).
 *
 * Returns the manifest's missing count and the documents that carry last
 * accepted text (R0-A) so the caller can say so; a pack always exports,
 * missing items and all (it warns, it never blocks).
 */
export async function exportSubmissionPack(
  bidId: string,
  templateId?: string,
  options: ExportOptions = {},
): Promise<{ missingCount: number; pendingAiChanges: PackPendingAiChange[] }> {
  const { exportId } = await api.post<{ exportId: string }>(
    `/bids/${bidId}/submission-pack/export`,
    templateId
      ? { templateId, ...(options.acknowledgeTemplateLoss ? { acknowledgeTemplateLoss: true } : {}) }
      : {}
  )
  const result = await pollPackExport(bidId, exportId)
  return { missingCount: result.missingCount ?? 0, pendingAiChanges: result.pendingAiChanges ?? [] }
}

/**
 * "Download files" on the Submit page (R3 T6a): a ZIP of exactly the file each
 * checklist item will send, copied as kept — nothing is re-rendered. Same
 * start + poll as the pack. Downloading never marks the bid lodged.
 */
export async function downloadSelectedSubmissionFiles(bidId: string): Promise<void> {
  const { exportId } = await api.post<{ exportId: string }>(
    `/bids/${bidId}/submission-pack/export`,
    { files: 'selected' }
  )
  await pollPackExport(bidId, exportId)
}

/** Poll a pack export until it settles, then fire the browser download. */
async function pollPackExport(
  bidId: string,
  exportId: string
): Promise<Extract<PackExportStatusResponse, { status: 'complete' }>> {
  const deadline = Date.now() + PACK_EXPORT_POLL_TIMEOUT_MS
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_INTERVAL_MS))

    const result = await api.get<PackExportStatusResponse>(
      `/bids/${bidId}/submission-pack/export/status?exportId=${exportId}`
    )

    if (result.status === 'complete') {
      const a = document.createElement('a')
      a.href = result.downloadUrl
      a.download = result.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      return result
    }

    if (result.status === 'failed') {
      throw new ApiRequestError({ code: result.code, message: result.message })
    }

    if (Date.now() >= deadline) {
      throw new ApiRequestError({
        code: 'PACK_EXPORT_TIMEOUT',
        message: 'The pack export is taking longer than expected. Please try again.',
      })
    }
  }
}

// ===================
// Submit: exact files and Mark lodged (R3 T6a/T6b)
// ===================

/** Each checklist item's file with its checks, and every lodgement record. */
export async function getSubmissionFiles(bidId: string): Promise<SubmissionFilesResponse> {
  return api.get<SubmissionFilesResponse>(`/bids/${bidId}/submission-files`)
}

/** Pin the version an item sends; null goes back to the newest file. */
export async function setSubmissionFile(
  bidId: string,
  itemId: string,
  outputVersionId: string | null
): Promise<void> {
  await api.patch(`/bids/${bidId}/submission-files/${itemId}`, { outputVersionId })
}

/** Mark lodged: freeze copies of the chosen files. Does not move the bid's status. */
export async function lodgeSubmission(
  bidId: string,
  input: LodgeSubmissionInput
): Promise<SubmissionRecord> {
  const { record } = await api.post<{ record: SubmissionRecord }>(`/bids/${bidId}/submission-records`, input)
  return record
}

export async function updatePackItem(
  bidId: string,
  itemId: string,
  patch: PackItemPatch
): Promise<void> {
  await api.patch(`/bids/${bidId}/extraction/submission_checklist/${itemId}`, patch)
}

export async function createPackItem(
  bidId: string,
  input: CreatePackItemInput
): Promise<{ item: { id: string } }> {
  return api.post<{ item: { id: string } }>(
    `/bids/${bidId}/extraction/submission_checklist/manual`,
    input
  )
}

/**
 * Hard-delete one checklist row. Only ever called for `extractionSource: 'manual'`
 * rows — extracted rows are dismissed on Analysis, never deleted from the Pack.
 */
export async function deletePackItem(bidId: string, itemId: string): Promise<void> {
  await api.delete(`/bids/${bidId}/extraction/submission_checklist/${itemId}`)
}

/**
 * Body for a new ask.
 *
 * `artifactId` + `sectionId` are REQUIRED for `kind: 'todo'` and REJECTED for
 * `kind: 'manual'` — the server 400s either way round. An overlay POST is
 * idempotent on its `(artifact, section, text)` key: hitting an existing
 * overlay returns that row unchanged rather than inserting a twin, so the
 * caller must PATCH the id it gets back, not assume a fresh one.
 */
export interface CreatePackAskInput {
  /** `fact` asks are created by the drafting job only — the server refuses them here. */
  kind: Exclude<PackAskKind, 'fact' | 'question'>
  text: string
  artifactId?: string
  sectionId?: string
  ownerId?: string | null
}

/** `resolved` is a flag, not a timestamp — the server stamps `resolved_at` itself. */
export interface PackAskPatch {
  answer?: string | null
  ownerId?: string | null
  resolved?: boolean
}

export async function listPackAsks(bidId: string): Promise<{ asks: PackAsk[] }> {
  return api.get<{ asks: PackAsk[] }>(`/bids/${bidId}/pack-asks`)
}

export async function createPackAsk(
  bidId: string,
  input: CreatePackAskInput
): Promise<{ ask: PackAsk }> {
  return api.post<{ ask: PackAsk }>(`/bids/${bidId}/pack-asks`, input)
}

export async function updatePackAsk(
  bidId: string,
  askId: string,
  patch: PackAskPatch
): Promise<{ ask: PackAsk }> {
  return api.patch<{ ask: PackAsk }>(`/bids/${bidId}/pack-asks/${askId}`, patch)
}

// --- Bid memory (AI redesign TD §3.14) ---

/** 'chat': saved by the AI (`remember`). 'manual': added from the Memory list. 'ask_card': Stage 2. */
export type BidMemorySourceKind = 'chat' | 'ask_card' | 'manual'

/**
 * One thing the bid team told the AI. Every AI door on the bid reads the live
 * items. Render the author with `getUserDisplayName({ userId: createdBy,
 * fullName: createdByName, email: createdByEmail }, currentUserId)` so the
 * current user shows as "You"; `createdBy` is null once that person is removed.
 */
export interface BidMemoryItem {
  id: string
  bidId: string
  scope: 'bid'
  content: string
  sourceKind: BidMemorySourceKind
  createdBy: string | null
  createdByName: string | null
  createdByEmail: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Outcome of a delete. `already_deleted` (someone removed it first) and
 * `not_found` (no such item on this bid any more) both mean the item is gone:
 * show "Already removed", not an error.
 */
export type BidMemoryDeleteStatus = 'deleted' | 'already_deleted' | 'not_found'

/** Live items, oldest first. Anyone who can see the bid can read them. */
export async function listBidMemory(bidId: string): Promise<{ items: BidMemoryItem[] }> {
  return api.get<{ items: BidMemoryItem[] }>(`/bids/${bidId}/memory`)
}

/** Add from the Memory list. 1–1,000 characters after trimming; bid write access (403 otherwise). */
export async function createBidMemoryItem(bidId: string, content: string): Promise<{ item: BidMemoryItem }> {
  return api.post<{ item: BidMemoryItem }>(`/bids/${bidId}/memory`, { content })
}

/** Edit the text. 404 when the item was removed in the meantime. */
export async function updateBidMemoryItem(
  bidId: string,
  memoryId: string,
  content: string
): Promise<{ item: BidMemoryItem }> {
  return api.patch<{ item: BidMemoryItem }>(`/bids/${bidId}/memory/${memoryId}`, { content })
}

/**
 * Remove an item: the Memory list's delete and the panel's Undo. Idempotent on
 * the server, and a 404 comes back as `not_found` rather than an error, so two
 * people acting on the same item never see a failure.
 */
export async function deleteBidMemoryItem(
  bidId: string,
  memoryId: string
): Promise<{ status: BidMemoryDeleteStatus }> {
  try {
    return await apiRequest<{ status: 'deleted' | 'already_deleted' }>(`/bids/${bidId}/memory/${memoryId}`, {
      method: 'DELETE',
      silentStatuses: [404],
    })
  } catch (error) {
    if (error instanceof ApiRequestError && error.statusCode === 404) {
      return { status: 'not_found' }
    }
    throw error
  }
}
